import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { Container, Row, Col, Table, Spinner, Alert } from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faTrash,
  faListAlt,
  faClock,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faPlus,
  faBuilding,
  faBox,
  faCodeBranch,
  faTag,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import './BugScrubTool.css';
import Heading from '../common/Heading';
import Comments from '../bugscrub/Comments';
import CommentModal from '../bugscrub/CommentModal';
import DeleteProjectModal from '../bugscrub/DeleteProjectModal';
import ExportModal from '../bugscrub/ExportModal';
import BsaEnvironmentIndicator from '../bsa/BsaEnvironmentIndicator';
import ODD from '../../constants/odd';
import BZAPIService from '../../services/BZAPIService';
import { featureIsReadOnlyCheck } from '../../helpers/rbac';
import { TenantSettingsContext } from '../../context/TenantSettingsContext';
import InventoryFetchModal from '../bsa/inventory/InventoryFetchModal';
import InventoryConfirmStep from '../bsa/inventory/InventoryConfirmStep';
import InventoryFleetStats from '../bsa/inventory/InventoryFleetStats';
import InventoryDriftAlert from '../bsa/inventory/InventoryDriftAlert';
import ComponentProfilePanel from '../bsa/inventory/ComponentProfilePanel';
import useProjectRefresh from '../bsa/inventory/useProjectRefresh';
import useComponentOverride from '../bsa/inventory/useComponentOverride';
import { buildProductGroups } from '../bsa/inventory/productDropdownGroups';
import {
  readInventoryDeepLink,
  readVendorProductDeepLink,
  resolveDeepLinkAction,
  findProductOption
} from '../bsa/inventory/inventoryDeepLink';
import useConfigRemovedOverlay from '../bsa/inventory/useConfigRemovedOverlay';
import { effectiveState, partialHintFor } from '../bsa/inventory/overlayMerge';
import ComponentCell, { ComponentColumnHeader } from '../bsa/inventory/ComponentCell';
import { componentCellData } from '../bsa/inventory/componentColumn';
import { hasInventory } from '../bsa/inventory/fleetPanelData';
import { projectProductNames } from '../bsa/inventory/projectProductNames';
import { BUG_CVE_REVIEW_STATES, CONFIG_REMOVED, AUTO_CONFIG_REMOVED, isStruckOut } from '../bsa/inventory/reviewStates';
import { ensureVersionRow } from '../bsa/inventory/ensureVersionRow';
const BugScrubbingTool = () => {
  // URL params and navigation
  const { projectId: urlProjectId, versionId: urlVersionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State for form controls and accordion
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [savingProject, setSavingProject] = useState(false);
  const [savingBugCve, setSavingBugCve] = useState(false);
  const [overrideError, setOverrideError] = useState(null);
  const [savingVersionState, setSavingVersionState] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const { bsaEnabled, isLoaded: tenantSettingsLoaded } = useContext(TenantSettingsContext);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [step, setStep] = useState(1);
  const [projectName, setProjectName] = useState('');
  const [showBugComment, setShowBugComment] = useState(null);
  const [showCveComment, setShowCveComment] = useState(null);
  const [showDeleteProject, setShowDeleteProject] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [bugStates, setBugStates] = useState({});
  const [cveStates, setCveStates] = useState({});
  // const [versionStatus, setVersionStatus] = useState('Not reviewed');
  const [userVersionData, setUserVersionData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [loadingAccordion, setLoadingAccordion] = useState(true);
  const [bugs, setBugs] = useState([]);
  const [cves, setCves] = useState([]);
  const [bugAverage, setBugAverage] = useState(null);
  const [cveAverage, setCveAverage] = useState(null);
  const [product, setProduct] = useState(null);
  const [version, setVersion] = useState(null);
  const [projectVersion, setProjectVersion] = useState(null); // The project's saved/original version (doesn't change when user selects versions in dropdown)
  const [products, setProducts] = useState([]); // For products fetched dynamically
  const [managedProducts, setManagedProducts] = useState([]);
  const [scanTarget, setScanTarget] = useState(null); // {id, name} while the modal is open
  const [scanResult, setScanResult] = useState(null); // snapshot once the scan succeeds
  const { refreshing, refreshError, applyRefresh, clearRefreshError } = useProjectRefresh();
  const { saving: savingComponent, toggleError, applyToggle, clearToggleError } = useComponentOverride();
  const { overlay, overlayError, loadOverlay, clearOverlay } = useConfigRemovedOverlay();
  const [refreshTarget, setRefreshTarget] = useState(null); // {id, name} while a refresh scan runs
  // Every inventory addition is gated on this one predicate. A project created
  // the ordinary way has no snapshot, so it must look exactly as it did before
  // this epic — no fleet tiles, no components card, and no extra column, which
  // would otherwise show an em dash on every row and read as missing data.
  const showComponentColumn = hasInventory(selectedProject);
  // A project created from a managed product is about a device, and a device
  // runs many modules — so its bug list is the union across them, not the one
  // module in productId. Falls back to the single name for manual projects and
  // for any managed product with no module data, so only device-scoped
  // projects change behaviour and every existing request shape is preserved.
  // Read through refs, not through the render closure. The bug fetches run
  // inside handleProjectSelection, which is created in a render where
  // managedProducts is still [] — it is populated by fetchProducts moments
  // later in the same async chain. Capturing the value would silently fall
  // back to the single-module query on every project open, which is exactly
  // the bug this indirection exists to prevent.
  const managedProductsRef = useRef(managedProducts);
  managedProductsRef.current = managedProducts;
  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;
  const productRef = useRef(product);
  productRef.current = product;

  const resolveBugProductNames = () => {
    const names = projectProductNames(
      selectedProjectRef.current,
      managedProductsRef.current,
      productRef.current?.label
    );
    return names.length > 1 ? names : productRef.current?.label;
  };
  // What the header calls the product. For a project created from a managed
  // product that is the device — the thing the customer owns and recognises —
  // not the ODD product, which for F5 is one of the device's modules and read
  // as "Product: BIG-IP LTM" on a project about an i4600. The module is still
  // stored and still drives the query; it just stops being presented as the
  // product. Manual projects are unchanged.
  const headerProductLabel =
    (selectedProject?.managedProductId &&
      (managedProducts || []).find(mp => mp.id === selectedProject.managedProductId)?.name) ||
    product?.label;
  const [versions, setVersions] = useState([]); // For versions fetched dynamically
  const [upgradeVersions, setUpgradeVersions] = useState([]); // For versions fetched dynamically
  const [openAccordion, setOpenAccordion] = useState(''); // for managing accordion state
  const [loadingProject, setLoadingProject] = useState(false); // Track loading for products
  const [loadingVendors, setLoadingVendors] = useState(true); // Track loading for products
  const [loadingProducts, setLoadingProducts] = useState(false); // Track loading for products
  const [loadingVersions, setLoadingVersions] = useState(false); // Track loading for versions
  const [loadingUpgradeVersions, setLoadingUpgradeVersions] = useState(false); // Track loading for versions
  const [bugFilters, setBugFilters] = useState({}); // Track loading for versions
  const [cveFilters, setCveFilters] = useState({}); // Track loading for versions
  const [bugFilterMeta, setBugFilterMeta] = useState([]); // Track loading for versions
  const [cveFilterMeta, setCveFilterMeta] = useState([]); // Track loading for versions
  const [viewingVersionDetail, setViewingVersionDetail] = useState(false); // Track if viewing version detail screen
  const scrollPositionRef = useRef(0); // Store scroll position when entering detail view
  const loadedProjectIdRef = useRef(null); // Tracks the project ID loaded into selectedProject so the URL effect won't re-load a project the user is exiting
  const openedVersionKeyRef = useRef(null); // Tracks the version key opened from the URL so the effect won't re-open it
  const inventoryDeepLinkHandledRef = useRef(false); // Ensures the managed-product-page deep link is only ever acted on once
  const vendorProductDeepLinkHandledRef = useRef(false); // Ensures the ServiceNow CI-record deep link is only ever acted on once
  const [activeDetailTab, setActiveDetailTab] = useState('review'); // 'review' or 'comparison'
  const [comparisonVersions, setComparisonVersions] = useState([null, null]); // Two versions to compare against current
  const [comparisonDataCache, setComparisonDataCache] = useState({}); // Cache for comparison version data { [suggestionId]: { bugs, cves, bugAverage, cveAverage } }
  const [comparisonLoading, setComparisonLoading] = useState([false, false]); // Loading state for each comparison slot
  const [currentVersionData, setCurrentVersionData] = useState(null); // Data for the project's current version
  const [currentVersionLoading, setCurrentVersionLoading] = useState(false); // Loading state for current version
  const [projectsCurrentPage, setProjectsCurrentPage] = useState(1); // Pagination for projects
  const [projectsPerPage] = useState(12); // Items per page for projects
  const [projectsSearchTerm, setProjectsSearchTerm] = useState(''); // Search filter for projects

  // User cannot create/delete projects if: BSA is disabled OR user has read-only access.
  // Declared early because the inventory deep-link effect below needs it to gate the scan.
  const canModifyProjects = bsaEnabled && !isReadOnly;

  // Version detail tabs (bugs/cves)
  const [activeDataTab, setActiveDataTab] = useState('bugs'); // 'bugs' or 'cves' tab for version detail
  // Bug search and pagination
  const [bugSearchTerm, setBugSearchTerm] = useState('');
  const [bugCurrentPage, setBugCurrentPage] = useState(1);
  const [bugsPerPage] = useState(10);
  // CVE search and pagination
  const [cveSearchTerm, setCveSearchTerm] = useState('');
  const [cveCurrentPage, setCveCurrentPage] = useState(1);
  const [cvesPerPage] = useState(10);

  const filterByValueOption = (option, inputValue) => {
    // Search only in the `value` field
    return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
  };
  const handleProjectSelection = useCallback(
    async (project, skipNavigation = false) => {
      loadedProjectIdRef.current = project.id;
      setLoadingProject(true);
      // A refresh error belongs to the project it happened on. BugScrubTool
      // is not remounted when moving between /plan and /plan/:projectId, so
      // useProjectRefresh's error state would otherwise survive into
      // whatever project is opened next and be misread as its own
      // (STRY0038250 final-review finding 3).
      clearRefreshError();
      // Same reasoning for the component-panel's toggle error
      // (STRY0038263 final-review finding 3).
      clearToggleError();
      try {
        // get project detail
        const projectDetail = await BZAPIService.bsaGetProject(project.id);
        setSelectedProject(projectDetail);
        console.log('selectedProject', project);
        console.log('vendors', vendors);
        // Fall back to the project's own saved fields when the ODD lookups are
        // unavailable or don't contain a match — a failed getBstVendors /
        // getBstVendorProducts call must not block opening a saved project.
        const selectedVendor = vendors.find(vendor => vendor.value === project.vendorId) || {
          value: project.vendorId,
          label: project.vendorId
        };
        console.log('selectedVendor', selectedVendor);
        setVendor(selectedVendor);
        // fetchProducts returns { productOptions, managedProducts } here, and
        // undefined when the ODD lookup fails — destructure with a default
        // rather than dev's `|| []`, which would leave an object in `products`.
        const { productOptions: products = [] } = (await fetchProducts(selectedVendor)) || {};
        console.log('products', products);
        const selectedProduct = products.find(product => product.value === project.productId) || {
          value: project.productId,
          label: project.productName
        };
        setProduct(selectedProduct);
        console.log('selectedProduct', selectedProduct);
        const versions = (await fetchVersions(selectedProduct, selectedVendor)) || []; // Fetch versions for the selected product
        console.log('versions x', versions);
        const selectedVersion = versions.find(version => version.value === project.versionId);
        console.log('selectedVersion', selectedVersion);
        setVersion(selectedVersion);
        setProjectVersion(selectedVersion); // Store the project's saved version separately
        if (selectedVersion) {
          await fetchUpgradeVersions(selectedVersion, selectedVendor, selectedProduct); // Fetch versions for the selected product
        }
        setProjectName(project.name);
        setStep(2);
      } catch (error) {
        console.error('Failed to open project:', error);
        toast.error('Failed to load project data');
      } finally {
        setLoadingProject(false);
      }

      // Update URL to include project ID (unless we're loading from URL)
      if (!skipNavigation) {
        navigate(`/plan/${project.id}`, { replace: false });
      }
    },
    [vendors, navigate, clearRefreshError, clearToggleError]
  );
  const reviewStateOptions = [
    { value: 'Not reviewed', label: 'Not reviewed' },
    { value: 'In review', label: 'In review' },
    { value: 'Declined', label: 'Declined' },
    { value: 'Accepted', label: 'Accepted' },
    { value: 'Uncertain', label: 'Uncertain' }
  ];
  // const bsaCreateVersionComment = async project => {
  // BZAPIService.bsaCreateVersionComment()
  // }

  // BZAPIService.bsaDeleteVersionComment()
  // BZAPIService.bsaDeleteBugOrCveComment()
  // BZAPIService.bsaCreateBugOrCveComment()
  // BZAPIService.bsaCreateProject()
  // BZAPIService.bsaSetVersionState()
  // BZAPIService.bsaListProjects()
  // BZAPIService.bsaGetProject()
  // BZAPIService.bsaListProjectVersions()

  const handleSetVersionState = async data => {
    console.log('handleSetVersionState', data, selectedProject.versions, data.versionId);
    await BZAPIService.bsaSetVersionState(data);
    const projectDetail = await BZAPIService.bsaGetProject(selectedProject.id);
    setSelectedProject(projectDetail);

    // update local state optimistically

    setUserVersionData({ ...userVersionData, state: data.state });
  };

  const resetSelectedVersion = () => {
    setSelectedVersion(null);
    setBugAverage(null);
    setCveAverage(null);
    setBugs([]);
    setCves([]);
    setUserVersionData(null);
    setBugStates({});
    setCveStates({});
    // Finding 1 (STRY0038263 final-review): the overlay is fetched by
    // project + version and must never outlive the version it was computed
    // for. toggleAccordion calls this on every version open — including
    // switching straight from version A to version B — so without this the
    // overlay could still hold A's marks while bugStates/cveStates above are
    // already cleared for B, and a fetch failure for B (which never reaches
    // loadOverlay) would leave A's stale marks showing against B's issues.
    clearOverlay();
    // Finding 3: an override error belongs to the version it happened on,
    // not whatever version is opened next.
    setOverrideError(null);
  };
  const handleStartCreateProject = async project => {
    setCreatingProject(true);
    setSelectedProject(null);
    setVendor(null);
    setProduct(null);
    setVersion(null);
    setProjectVersion(null);
    setStep(2);
  };

  // Cancelling out of the CMDB scan modal or the confirm step. The scan can
  // be reached two ways: the managed-product-page deep link (lands directly
  // on step 2 with no selectedProject and creatingProject=false), or picking
  // a managed product from the dropdown inside the manual create form
  // (creatingProject=true). Only the first case has nothing else left on
  // screen once the scan state clears — every child of the step===2 block
  // is false, so it renders empty with no way back. Reset to the project
  // listing in that case; leave the manual create form alone otherwise.
  const handleInventoryCancel = () => {
    setScanTarget(null);
    setScanResult(null);
    if (!selectedProject && !creatingProject) {
      setStep(1);
      navigate('/plan', { replace: false });
    }
  };
  const handleCreateProject = async () => {
    setSavingProject(true);
    const project = {
      vendorId: vendor.value,
      versionId: version.value,
      name: projectName,
      productName: product.label,
      productId: product.value
    };

    const createdProject = await BZAPIService.bsaCreateProject(project);
    setSavingProject(false);
    setProjects([...projects, createdProject]);
    handleProjectSelection(createdProject);
    setCreatingProject(false);
  };

  // API Call to Fetch Projects
  const fetchProjects = async () => {
    try {
      const response = await BZAPIService.bsaListProjects();
      console.log('projects', response);
      setProjects(response);
      setLoadingProjects(false);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };
  // API Call to Fetch Products
  const fetchVendors = async () => {
    try {
      const data = await BZAPIService.bsaGetBstVendors();
      console.log('vendors', data);
      // Example response: [{ value: 'nexus-9000', label: 'Nexus 9000 Switch' }]
      if (data?.length) {
        const vendorOptions = data.map(vendor => ({
          value: vendor.vendorId,
          label: vendor.label
        }));
        console.log('vendorOptions', vendorOptions);
        setVendors(vendorOptions);
      }
      setLoadingVendors(false); // End loading
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
      setLoadingVendors(false); // End loading
    }
  };
  // API Call to Fetch Products
  const fetchProducts = async selectedVendor => {
    setLoadingProducts(true); // Start loading
    setProduct(null);
    setVersion(null);
    setVersions([]);
    try {
      const data = await BZAPIService.bsaGetBstVendorProducts(selectedVendor.value);
      console.log('products res', data);
      // Example response: [{ value: 'nexus-9000', label: 'Nexus 9000 Switch' }]
      if (data?.results) {
        const productOptions = data.results.map(product => ({
          value: product.productId,
          label: product.productName
        }));
        console.log('productOptions', productOptions);
        setProducts(productOptions);
        let managedProducts = [];
        try {
          const mps = await BZAPIService.listManagedProducts(selectedVendor.value);
          managedProducts = Array.isArray(mps) ? mps : [];
        } catch (e) {
          // Inventory is an enhancement; the manual flow must still work.
          managedProducts = [];
        }
        setManagedProducts(managedProducts);
        setLoadingProducts(false); // End loading

        // Returning managedProducts alongside productOptions lets callers that
        // need the freshly-fetched list (e.g. the CI deep-link effect) use it
        // directly instead of re-fetching it a second time for the same
        // vendor — see the deep-link effect below.
        return { productOptions, managedProducts };
      }
      setLoadingProducts(false); // End loading
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setLoadingProducts(false); // End loading
    }
  };

  // API Call to Fetch Versions
  const fetchVersions = async (selectedProduct, vendor) => {
    setVersion(null);
    try {
      const data = await BZAPIService.bsaGetBstSoftwareSuggestions(vendor.value, selectedProduct.value);
      console.log('versions', data);
      if (data?.results) {
        const versionOptions = data.results.map(product => ({
          value: product.suggestionId,
          labelBug: product.labelBug,
          labelCve: product.labelCve,
          label: product.releaseVersion
        }));
        setVersions(versionOptions);
        setLoadingVersions(false);

        return versionOptions;
      }
      setLoadingVersions(false);
      // Example response: [{ value: '15.0', label: '15.0(1k)' }]
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      setLoadingVersions(false);
    }
  };
  const fetchCves = async selectedVersion => {
    try {
      const data = await BZAPIService.bsaGetBstCves(
        vendor.value,
        selectedVersion.softwareType,
        selectedVersion.labelCve
      );
      console.log('fetchCves', data);
      if (data?.results) {
        setCveAverage(data?.cvssAverage);
        console.log('cveAverage', data?.cvssAverage);
        setCves(data.results);
      }
      // Return data for caching
      return {
        cves: data?.results || [],
        cveAverage: data?.cvssAverage || null
      };
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      return { cves: [], cveAverage: null };
    }
  };
  const fetchBugs = async selectedVersion => {
    try {
      const data = await BZAPIService.bsaGetBstBugs(vendor.value, resolveBugProductNames(), selectedVersion.labelBug);
      console.log('fetchBugs', data);
      if (data?.results) {
        setBugAverage(data?.riskScoreAverage);
        setBugs(data.results);
      }
      // Return data for caching
      return {
        bugs: data?.results || [],
        bugAverage: data?.riskScoreAverage || null
      };
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      return { bugs: [], bugAverage: null };
    }
  };

  // Fetch comparison version data (bugs and CVEs) and cache it
  const fetchComparisonVersionData = async (comparisonVersion, slotIndex) => {
    if (!comparisonVersion) return;

    const suggestionId = comparisonVersion.suggestionId;

    // Check if already cached
    if (comparisonDataCache[suggestionId]) {
      return comparisonDataCache[suggestionId];
    }

    // Set loading state for this slot
    setComparisonLoading(prev => {
      const newLoading = [...prev];
      newLoading[slotIndex] = true;
      return newLoading;
    });

    try {
      // Fetch bugs and CVEs in parallel
      const [bugsData, cvesData] = await Promise.all([
        BZAPIService.bsaGetBstBugs(vendor.value, resolveBugProductNames(), comparisonVersion.labelBug),
        BZAPIService.bsaGetBstCves(vendor.value, comparisonVersion.softwareType, comparisonVersion.labelCve)
      ]);

      const versionData = {
        bugs: bugsData?.results || [],
        cves: cvesData?.results || [],
        bugAverage: bugsData?.riskScoreAverage || null,
        cveAverage: cvesData?.cvssAverage || null
      };

      // Cache the data
      setComparisonDataCache(prev => ({
        ...prev,
        [suggestionId]: versionData
      }));

      return versionData;
    } catch (error) {
      console.error('Failed to fetch comparison version data:', error);
      return null;
    } finally {
      // Clear loading state for this slot
      setComparisonLoading(prev => {
        const newLoading = [...prev];
        newLoading[slotIndex] = false;
        return newLoading;
      });
    }
  };

  // Fetch the project's current version data for comparison baseline
  const fetchCurrentVersionData = async () => {
    if (!projectVersion || currentVersionData) return; // Already loaded or no project version

    setCurrentVersionLoading(true);

    try {
      // Fetch bugs and CVEs in parallel for the project's saved/original version
      const [bugsData, cvesData] = await Promise.all([
        BZAPIService.bsaGetBstBugs(vendor.value, resolveBugProductNames(), projectVersion.labelBug),
        BZAPIService.bsaGetBstCves(vendor.value, projectVersion.softwareType, projectVersion.labelCve)
      ]);

      const versionData = {
        bugs: bugsData?.results || [],
        cves: cvesData?.results || [],
        bugAverage: bugsData?.riskScoreAverage || null,
        cveAverage: cvesData?.cvssAverage || null
      };

      setCurrentVersionData(versionData);
      return versionData;
    } catch (error) {
      console.error('Failed to fetch current version data:', error);
      return null;
    } finally {
      setCurrentVersionLoading(false);
    }
  };

  const fetchUserVersionState = async selectedVersion => {
    console.log('fetchUserVersionStatex', selectedVersion);
    // to get version state we must find it in the selected project

    try {
      const userVersionState = selectedProject.versions.find(
        version => version.versionId === selectedVersion.suggestionId
      );
      console.log('userVersionState', userVersionState);
      if (userVersionState) {
        console.log('found fetchUserVersionState', userVersionState);
        const response = await BZAPIService.bsaListProjectVersions(userVersionState.id);
        console.log('BZAPIService.bsaListProjectVersions', response);
        setUserVersionData({ ...response });
        // Iterate the bugs and set the state for each bug
        const bugStates = {};
        response.bugs.forEach(bug => {
          console.log('bug', bug);
          bugStates[bug.bugId] = { value: bug.state, edit: false };
        });
        console.log('bugStates', bugStates);
        setBugStates(bugStates);
        // Iterate the cves and set the state for each cve
        const cveStates = {};
        response.cves.forEach(cve => {
          cveStates[cve.cveId] = { value: cve.state, edit: false };
        });
        setCveStates(cveStates);

        /*
        {
          "id": "6a5d5a9d-50dd-4c3c-bdd8-9064ea370bc4",
          "versionId": "Everest-16.6.2",
          "updatedAt": "2025-02-22 23:15:23",
          "createdAt": "2025-02-22 23:15:23",
          "productName": "1100 Integrated Services Router",
          "productId": "286315006",
          "state": "Declined",
          "comments": [],
          "activityLog": [
              {
                  "field": "",
                  "value": "",
                  "change": "Created project version",
                  "userId": "dev-chase@findbugzero.com",
                  "timestamp": "2025-02-22T23:15:22.813405"
              },
              {
                  "field": "state",
                  "value": "Declined",
                  "change": "State set to Declined",
                  "userId": "dev-chase@findbugzero.com",
                  "timestamp": "2025-02-22T23:15:22.813417"
              }
          ],
          "bugs": [],
          "cves": []
      }
          */
      } else {
        // If version does not exist, create it by setting the state to "Not reviewed"
        console.log('create version');
        const response = await BZAPIService.bsaSetVersionState({
          versionId: selectedVersion.suggestionId,
          projectId: selectedProject.id,
          productName: selectedProject.productName,
          productId: selectedProject.productId,
          state: 'Not reviewed'
        });
        // Set userVersionData
        console.log('BZAPIService.bsaSetVersionState create', response);

        // Refetch the project to get the new version
        const projectDetail = await BZAPIService.bsaGetProject(selectedProject.id);
        console.log('projectDetail', projectDetail);
        setSelectedProject(projectDetail);
        const responseProjectVersion = await BZAPIService.bsaListProjectVersions(response.projectVersionId);
        console.log('BZAPIService.bsaListProjectVersions', responseProjectVersion);
        setUserVersionData({ ...responseProjectVersion });
      }

      // The overlay is fetched by project + suggestion id, so it works whether
      // or not a bsaProjectVersions row exists yet — first open included.
      // Finding 4 (STRY0038263 final-review): automatic marking only exists
      // for inventory-linked projects (a CMDB scan is what produces the
      // component profile it's computed from). Calling this for an ordinary
      // project has nothing to gain and, on a failure, renders "Automatic
      // marking is unavailable for this version." in a review that never had
      // automatic marking to begin with.
      if (selectedProject && selectedProject.managedProductId && selectedVersion && selectedVersion.suggestionId) {
        await loadOverlay(selectedProject.id, selectedVersion.suggestionId);
      }

      // Example response: [{ value: '15.0', label: '15.0(1k)' }]
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      setUserVersionData(null);
    }
  };

  // API Call to Fetch Versions
  const fetchUpgradeVersions = async (selectedVersion, vendor, product) => {
    console.log(selectedVersion, 'selectedVersion');
    // setVersion(null);
    console.log(vendor.value, 'vendor.value');
    console.log(product.value, 'product.value');
    console.log(selectedVersion.value, 'selectedVersion.value');
    try {
      const data = await BZAPIService.bsaGetBstSoftwareSuggestions(vendor.value, product.value, selectedVersion.value);
      console.log('versions', data);
      if (data?.results) {
        console.log('upgradeVersions', data.results);
        setUpgradeVersions(data.results);
        setBugFilterMeta(data?.metadata?.filters?.bug);
        setCveFilterMeta(data?.metadata?.filters?.cve);
        console.log('cveFilterMeta', data?.metadata?.filters?.cve);
        console.log('versionFilterMeta', data?.metadata?.filters?.version);
      }
      setLoadingUpgradeVersions(false);
      // Example response: [{ value: '15.0', label: '15.0(1k)' }]
    } catch (error) {
      console.error('Failed to fetch upgrade versions:', error);
      setLoadingUpgradeVersions(false);
    }
  };

  // Toggle accordion for each version selectVersion
  const toggleAccordion = async id => {
    console.log('toggleAccordion', id);
    console.log('upgradeVersion expanded', upgradeVersions[id]);
    const selectedVersion = upgradeVersions[id];
    // fetch bugs and cves
    setLoadingAccordion(true);
    const openAccordionId = openAccordion === id ? '' : id;
    setOpenAccordion(openAccordionId);
    // Reset selected Version data
    resetSelectedVersion();
    if (openAccordionId) {
      const [cveData, bugData] = await Promise.all([
        fetchCves(selectedVersion),
        fetchBugs(selectedVersion),
        fetchUserVersionState(selectedVersion)
      ]);

      // Cache the fetched data for display in the version listing
      if (selectedVersion?.suggestionId) {
        setComparisonDataCache(prev => ({
          ...prev,
          [selectedVersion.suggestionId]: {
            bugs: bugData?.bugs || [],
            cves: cveData?.cves || [],
            bugAverage: bugData?.bugAverage,
            cveAverage: cveData?.cveAverage
          }
        }));
      }

      setSelectedVersion(selectedVersion);
      setBugFilters({});
      setCveFilters({});
    }
    setLoadingAccordion(false);
  };

  // Function to determine color and label based on risk score
  const getRiskDetails = score => {
    if (score >= 8) return { color: 'red', label: 'High' };
    if (score >= 5) return { color: 'orange', label: 'Medium' };
    if (score > 0) return { color: 'green', label: 'Low' };
    return { color: 'grey', label: 'Not available' };
  };
  const getUniqueCveOptions = key => {
    const uniqueValues = Array.from(
      new Set(cves.map(item => item[key]).filter(value => value !== undefined))
      // Natural alphanumeric sort so "1-…", "2-…" order correctly.
    ).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
    return uniqueValues.map(value => ({
      value,
      label: value
    }));
  };

  const getFilteredCves = () => {
    let filtered = cves;

    // Apply search filter
    if (cveSearchTerm) {
      const searchLower = cveSearchTerm.toLowerCase();
      filtered = filtered.filter(
        cve =>
          cve.id?.toLowerCase().includes(searchLower) ||
          cve.title?.toLowerCase().includes(searchLower) ||
          cve.severity?.toLowerCase().includes(searchLower)
      );
    }

    // Apply meta filters
    if (cveFilters && Object.keys(cveFilters).length > 0) {
      filtered = filtered.filter(cve => {
        return Object.entries(cveFilters).every(([key, selectedValues]) => {
          if (selectedValues.length === 0) return true;
          return selectedValues.includes(cve[key]);
        });
      });
    }

    return filtered;
  };

  // Get paginated CVEs
  const getPaginatedCves = () => {
    const filtered = getFilteredCves();
    const startIndex = (cveCurrentPage - 1) * cvesPerPage;
    const endIndex = startIndex + cvesPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Get total pages for CVEs
  const getCveTotalPages = () => {
    return Math.ceil(getFilteredCves().length / cvesPerPage);
  };

  const getUniqueBugOptions = key => {
    const uniqueValues = Array.from(
      new Set(bugs.map(item => item[key]).filter(value => value !== undefined))
      // Natural alphanumeric sort so "1-Blocking", "2-Critical", … "5-Cosmetic" order correctly.
    ).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
    return uniqueValues.map(value => ({
      value,
      label: value
    }));
  };

  const getFilteredBugs = () => {
    let filtered = bugs;

    // Apply search filter
    if (bugSearchTerm) {
      const searchLower = bugSearchTerm.toLowerCase();
      filtered = filtered.filter(
        bug =>
          bug.id?.toLowerCase().includes(searchLower) ||
          bug.title?.toLowerCase().includes(searchLower) ||
          bug.severity?.toLowerCase().includes(searchLower)
      );
    }

    // Apply meta filters
    if (bugFilters && Object.keys(bugFilters).length > 0) {
      filtered = filtered.filter(bug => {
        return Object.entries(bugFilters).every(([key, selectedValues]) => {
          if (selectedValues.length === 0) return true;
          return selectedValues.includes(bug[key]);
        });
      });
    }

    return filtered;
  };

  // Get paginated bugs
  const getPaginatedBugs = () => {
    const filtered = getFilteredBugs();
    const startIndex = (bugCurrentPage - 1) * bugsPerPage;
    const endIndex = startIndex + bugsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Get total pages for bugs
  const getBugTotalPages = () => {
    return Math.ceil(getFilteredBugs().length / bugsPerPage);
  };

  // Set a variable with getFilteredBugs() and getFilteredCves() to get the average bugRiskScore and cvssAverage
  useEffect(() => {
    const filteredBugs = getFilteredBugs();
    const filteredCves = getFilteredCves();

    const calculateAverage = (items, key) => {
      if (items.length === 0) return null;
      let validItems = items.filter(item => item[key] !== undefined && item[key] !== null && item[key] !== '');
      // Filter further if state is Ignored, Not applicable
      console.log('valid items', validItems);
      console.log('userVersionData', userVersionData);
      if (key === 'bugRiskScore') {
        // filter validItems further if an item inside it is inside userVersionData.bugs matches the id and the state is Ignored or Not applicable
        validItems = validItems.filter(item => {
          const bugState = userVersionData?.bugs.find(bug => bug.bugId === item.id)?.state;
          return bugState !== 'Ignored' && bugState !== 'Not applicable';
        });
        console.log('valid items 2', validItems);
      }

      if (key === 'cvss_base_score') {
        validItems = validItems.filter(item => {
          const cveState = userVersionData?.cves.find(cve => cve.cveId === item.id)?.state;
          return cveState !== 'Ignored' && cveState !== 'Not applicable';
        });
      }

      if (validItems.length === 0) return null;
      const sum = validItems.reduce((acc, item) => acc + Number(item[key]), 0);
      return (sum / validItems.length).toFixed(2);
    };
    console.log('useEffect', calculateAverage(filteredBugs, 'bugRiskScore'));

    setBugAverage(calculateAverage(filteredBugs, 'bugRiskScore'));
    setCveAverage(calculateAverage(filteredCves, 'cvss_base_score'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bugFilters, cveFilters, bugs, cves, userVersionData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Check user permissions for bugscrubadvisor feature
        const myUserPermissions = await BZAPIService.getMyUserPermissions();
        setIsReadOnly(featureIsReadOnlyCheck('bugscrubadvisor', myUserPermissions));
      } catch (error) {
        console.error('Failed to fetch user permissions:', error);
        setIsReadOnly(true); // Default to read-only on error
      }

      // Fetch current user email for export modal
      try {
        const myUser = await BZAPIService.getMyUser();
        setCurrentUserEmail(myUser?.email || '');
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }

      fetchVendors();
      fetchProjects();
    };

    loadInitialData();
  }, []);

  // Open the scan modal when arriving via the managed-product-page deep link.
  // The modal only renders in the step-2 tree, so the deep link has to land
  // there too, not just set scanTarget.
  //
  // InventoryConfirmStep needs a vendor and a product list (the same ones the
  // manual flow loads via fetchProducts), so we resolve the managed product's
  // vendor and reuse fetchProducts rather than opening the modal bare.
  // Runs once — gated on the vendor list being loaded — and never again, so
  // cancelling doesn't reopen the modal.
  useEffect(() => {
    const link = readInventoryDeepLink(searchParams);
    if (!link || inventoryDeepLinkHandledRef.current) return;
    if (!vendors.length) return; // wait for the vendor list before deciding

    // bsaEnabled defaults optimistically to true until tenant settings
    // finish loading (isReadOnly correctly defaults to the safe `true`
    // instead). A positive canModifyProjects reading can't be trusted until
    // settings have actually loaded, or a pasted deep link on a
    // Plan-disabled tenant could slip through if vendors resolve first. A
    // negative reading is always safe to act on immediately.
    if (canModifyProjects && !tenantSettingsLoaded) return;

    if (!canModifyProjects) {
      // Read-only user or a Plan-disabled tenant: never start a CMDB scan
      // from the deep link. Deliberately not latched: canModifyProjects is
      // only ever false here because a dependency (isReadOnly, bsaEnabled)
      // hasn't resolved to its real value yet, or genuinely is disallowed.
      // Either way, if it later flips to true, this effect must be free to
      // reconsider rather than have that outcome permanently masked.
      return;
    }

    let cancelled = false;
    (async () => {
      let matchedVendor = null;
      try {
        const managedProduct = await BZAPIService.getManagedProduct(link.id);
        matchedVendor = managedProduct && vendors.find(v => v.value === managedProduct.vendorId);
        if (matchedVendor) {
          setVendor(matchedVendor);
          await fetchProducts(matchedVendor);
        }
      } catch (e) {
        // Inventory is an enhancement; degrade to opening the scan without vendor context
        // rather than blocking the deep link entirely.
      }
      // The ref is only ever latched here, once we're actually committing to
      // opening the scan — never on the blocked path above. So if
      // canModifyProjects flips false mid-flight (this run's cleanup already
      // set cancelled=true), the deep link isn't permanently swallowed: the
      // next run this triggers gets a clean chance to decide again, and can
      // still succeed if permission comes back.
      if (cancelled) return;
      inventoryDeepLinkHandledRef.current = true;
      setScanTarget(link);
      setStep(2);
    })();

    return () => {
      cancelled = true;
    };
    // Only once the vendor list is available: re-running would reopen the modal after
    // the user cancels.
  }, [vendors, canModifyProjects, tenantSettingsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // The ServiceNow CI-record link: vendor/product/version rather than a
  // managedProductId. Resolve it to one of the tenant's managed products and
  // run the normal scan; when the tenant does not track that product there is
  // nothing to scan, so pre-fill the manual form instead of dead-ending.
  useEffect(() => {
    const link = readVendorProductDeepLink(searchParams);
    if (!link || vendorProductDeepLinkHandledRef.current) return;
    if (!vendors.length) return;
    if (canModifyProjects && !tenantSettingsLoaded) return;
    if (!canModifyProjects) return;

    const matchedVendor = vendors.find(v => v.value === link.vendorId);
    if (!matchedVendor) return;

    let cancelled = false;
    (async () => {
      setVendor(matchedVendor);
      // fetchProducts already fetches this vendor's managed products
      // internally (matchedVendor.value === link.vendorId here) — reuse its
      // return value instead of calling listManagedProducts a second time
      // for the same vendor (STRY0038250 final-review finding 6).
      const { productOptions, managedProducts } = (await fetchProducts(matchedVendor)) || {};
      if (cancelled) return;

      const decision = resolveDeepLinkAction(searchParams, managedProducts);
      vendorProductDeepLinkHandledRef.current = true;
      setStep(2);

      if (decision.action === 'scan') {
        setScanTarget({ id: decision.managedProductId, name: decision.managedProductName });
        return;
      }

      // No managed product for this vendor/product: pre-fill what we were
      // given so the user still gets a project, just without a snapshot.
      // creatingProject is only set on this branch (not the scan branch
      // above) so cancelling a deep-link scan lands on the project listing,
      // same as the sibling managed-product deep link, instead of a
      // half-empty manual create form.
      setCreatingProject(true);
      const preselected = findProductOption(productOptions, link.productName);
      if (preselected) {
        setProduct(preselected);
        const versionOptions = await fetchVersions(preselected, matchedVendor);
        if (cancelled) return;
        const matchedVersion = (versionOptions || []).find(v => v.value === link.version);
        if (matchedVersion) {
          setVersion(matchedVersion);
          // Setting product/version directly here bypasses the version
          // Select's onChange, which is what normally derives the project
          // name — leaving it blank kept Create disabled until the user
          // typed one (STRY0038250 final-review finding 5). Derive it the
          // same way the manual flow does.
          setProjectName(`${matchedVendor.label} - ${preselected.label} - ${matchedVersion.label}`);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, vendors, canModifyProjects, tenantSettingsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reconcile the view with the URL — handles deep links and browser back/forward.
  //   /plan                         → project listing
  //   /plan/:projectId              → project detail (version list)
  //   /plan/:projectId/:versionId   → that version's detail
  useEffect(() => {
    // 1) No project in the URL → show the listing. If a project was loaded
    //    (e.g. the user pressed Back from a project), reset back to step 1.
    if (!urlProjectId) {
      loadedProjectIdRef.current = null;
      openedVersionKeyRef.current = null;
      if (selectedProject || viewingVersionDetail) {
        setSelectedProject(null);
        setViewingVersionDetail(false);
        clearOverlay();
        resetSelectedVersion();
        setStep(1);
      }
      return;
    }

    // 2) Project in the URL but not loaded yet → load it (skip nav, already here).
    if (loadedProjectIdRef.current !== urlProjectId) {
      // vendors may legitimately be empty (ODD vendor lookup down) —
      // handleProjectSelection falls back to the project's saved fields.
      if (projects.length > 0 && !loadingProjects && !loadingVendors) {
        const projectFromUrl = projects.find(p => p.id === urlProjectId);
        if (projectFromUrl) {
          handleProjectSelection(projectFromUrl, true);
        } else {
          console.warn('Project not found for URL ID:', urlProjectId);
          navigate('/plan', { replace: true });
        }
      }
      return; // wait for the project (and its upgrade versions) to load
    }

    // 3) Project is loaded → reconcile the version detail with the URL.
    if (urlVersionId) {
      if (!upgradeVersions.length) return; // upgrade versions not loaded yet
      const idx = upgradeVersions.findIndex(v => versionKey(v) === urlVersionId);
      if (idx === -1) {
        // Version not found for this project → fall back to the project detail.
        navigate(`/plan/${urlProjectId}`, { replace: true });
        return;
      }
      if (openedVersionKeyRef.current !== urlVersionId) {
        openedVersionKeyRef.current = urlVersionId;
        openVersionDetail(idx, true); // skip nav, already at this URL
      }
    } else if (viewingVersionDetail) {
      // Back from a version detail → return to the project's version list.
      openedVersionKeyRef.current = null;
      setViewingVersionDetail(false);
      clearOverlay();
      setOpenAccordion('');
      resetSelectedVersion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlProjectId,
    urlVersionId,
    projects,
    vendors,
    loadingProjects,
    loadingVendors,
    upgradeVersions,
    selectedProject
  ]);
  // Check if all selections are made for Step 1
  const isStep1Complete = vendor && product && version;

  // Calculate review progress stats
  const getReviewProgress = () => {
    if (!selectedProject?.versions || !upgradeVersions.length) {
      return { reviewed: 0, total: 0, percentage: 0 };
    }
    const total = upgradeVersions.length;
    const reviewed = selectedProject.versions.filter(v => v.state && v.state !== 'Not reviewed').length;
    const percentage = total > 0 ? Math.round((reviewed / total) * 100) : 0;
    return { reviewed, total, percentage };
  };

  // Get accepted version if any
  const getAcceptedVersion = () => {
    if (!selectedProject?.versions || !upgradeVersions.length) {
      return null;
    }
    const acceptedProjectVersion = selectedProject.versions.find(v => v.state === 'Accepted');
    if (acceptedProjectVersion) {
      const upgradeVersion = upgradeVersions.find(uv => uv.suggestionId === acceptedProjectVersion.versionId);
      return upgradeVersion?.releaseVersion || acceptedProjectVersion.versionId;
    }
    return null;
  };

  // Get version state from project
  const getVersionState = versionId => {
    return selectedProject?.versions?.find(v => v.versionId === versionId)?.state || 'Not reviewed';
  };

  // Get status badge class
  const getStatusBadgeClass = state => {
    switch (state) {
      case 'Accepted':
        return 'accepted';
      case 'Declined':
        return 'declined';
      case 'In review':
        return 'in-review';
      case 'Uncertain':
        return 'uncertain';
      default:
        return 'not-reviewed';
    }
  };

  // Get status badge symbol
  const getStatusSymbol = state => {
    switch (state) {
      case 'Accepted':
        return '✓';
      case 'Declined':
        return '✕';
      case 'In review':
        return '●';
      case 'Uncertain':
        return '?';
      default:
        return '○';
    }
  };

  // Calculate bug severity distribution using actual severity values from the data
  // Returns an ordered array to preserve the original filter meta sorting
  const getBugSeverityDistribution = () => {
    const distribution = [];
    let total = 0;

    // Find the severity filter key from bugFilterMeta
    const severityFilter = bugFilterMeta?.find(
      filter => filter.key?.toLowerCase() === 'severity' || filter.label?.toLowerCase() === 'severity'
    );
    const severityKey = severityFilter?.key || 'severity';

    // Get unique severity options in their original order from the data
    const severityOptions = getUniqueBugOptions(severityKey);

    // Count bugs for each severity option (preserving original order)
    if (bugs && bugs.length > 0) {
      severityOptions.forEach(option => {
        const count = bugs.filter(bug => bug[severityKey] === option.value).length;
        if (count > 0) {
          distribution.push({ severity: option.value, count });
          total += count;
        }
      });

      // Handle any bugs with unknown/missing severity
      const unknownCount = bugs.filter(bug => !bug[severityKey]).length;
      if (unknownCount > 0) {
        distribution.push({ severity: 'Unknown', count: unknownCount });
        total += unknownCount;
      }
    }

    return { distribution, total };
  };

  // Calculate CVE severity distribution using actual severity values from the data
  // Returns an ordered array with standard CVE severity ordering (Critical > High > Medium > Low)
  const getCveSeverityDistribution = () => {
    const distribution = [];
    let total = 0;

    // Find the severity filter key from cveFilterMeta
    const severityFilter = cveFilterMeta?.find(
      filter => filter.key?.toLowerCase() === 'severity' || filter.label?.toLowerCase() === 'severity'
    );
    const severityKey = severityFilter?.key || 'severity';

    // Standard CVE severity order (Critical first, then descending severity)
    const standardSeverityOrder = ['Critical', 'High', 'Medium', 'Low'];

    // Get all unique severity values from the data
    const severityOptions = getUniqueCveOptions(severityKey);
    const dataValues = severityOptions.map(opt => opt.value);

    // Count CVEs for each severity in standard order
    if (cves && cves.length > 0) {
      // First, add severities in standard order if they exist in the data
      standardSeverityOrder.forEach(severity => {
        if (dataValues.includes(severity)) {
          const count = cves.filter(cve => cve[severityKey] === severity).length;
          if (count > 0) {
            distribution.push({ severity, count });
            total += count;
          }
        }
      });

      // Then, add any remaining severities not in the standard order (preserving data order)
      dataValues.forEach(severity => {
        if (!standardSeverityOrder.includes(severity)) {
          const count = cves.filter(cve => cve[severityKey] === severity).length;
          if (count > 0) {
            distribution.push({ severity, count });
            total += count;
          }
        }
      });

      // Handle any CVEs with unknown/missing severity
      const unknownCount = cves.filter(cve => !cve[severityKey]).length;
      if (unknownCount > 0) {
        distribution.push({ severity: 'Unknown', count: unknownCount });
        total += unknownCount;
      }
    }

    return { distribution, total };
  };

  // Get distribution bar color based on position in array (first=red, last=green, gradient in between)
  const getDistributionBarColor = (index, totalItems) => {
    if (totalItems <= 1) return 'var(--bad)'; // Red for single item

    // Calculate position ratio (0 = first/red, 1 = last/green)
    const ratio = index / (totalItems - 1);

    // Color gradient: red -> orange -> yellow -> lime -> green
    if (ratio === 0) return 'var(--bad)'; // Red
    if (ratio <= 0.25) return 'var(--warn)'; // Orange
    if (ratio <= 0.5) return 'var(--warn)'; // Amber
    if (ratio <= 0.75) return 'var(--ok)'; // Lime
    return 'var(--ok)'; // Green
  };

  // Get risk level class
  const getRiskClass = score => {
    if (score >= 7) return 'high';
    if (score >= 4) return 'medium';
    return 'low';
  };

  // Stable URL identifier for an upgrade version (used in /plan/:projectId/:versionId).
  // suggestionId is the unique key the API returns; fall back to the version value.
  const versionKey = v => String(v?.suggestionId ?? v?.value ?? '');

  // Open a version's detail view by its index in upgradeVersions. Loads its
  // bugs/CVEs/review-state. When skipNavigation is false, also reflects it in the
  // URL so the version is directly linkable and survives reload/back-forward.
  const openVersionDetail = async (index, skipNavigation = false) => {
    setViewingVersionDetail(true);
    window.scrollTo(0, 0);
    if (!skipNavigation) {
      const v = upgradeVersions[index];
      openedVersionKeyRef.current = versionKey(v);
      navigate(`/plan/${selectedProject?.id || urlProjectId}/${versionKey(v)}`);
    }
    await toggleAccordion(`${index}`);
  };

  // Handle version row click - navigate to detail view
  const handleVersionClick = async (upgradeVersion, index) => {
    // Save current scroll position
    scrollPositionRef.current = window.scrollY;
    await openVersionDetail(index, false);
  };

  // Handle back button - return to the version list (and reflect it in the URL).
  const handleBackToList = () => {
    setViewingVersionDetail(false);
    clearOverlay();
    setOpenAccordion('');
    resetSelectedVersion();
    openedVersionKeyRef.current = null;
    navigate(`/plan/${selectedProject?.id || urlProjectId}`);
    // Restore scroll position after a brief delay to allow render
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 50);
  };

  if (savingProject) {
    return (
      <div className="text-center my-8">
        <Spinner color="primary" />
        <p>Creating your project...</p>
      </div>
    );
  }

  if (loadingProjects) {
    return (
      <div className="text-center my-8">
        <Spinner color="primary" />
        <p>Loading projects...</p>
      </div>
    );
  }
  return (
    <Container className="mt-4">
      {/* Header — env indicator (Prod/Dev) shows as a pill inline after the title. */}
      <div className="mt-3">
        <Heading
          title={'BugZero Plan'}
          subtitle={`Compare product versions by reviewing security and operational risks`}
          className="mb-4"
          icon="code-branch"
          loading={false}
          badge={<BsaEnvironmentIndicator />}
        />
      </div>
      {!canModifyProjects && (
        <Alert color="info" className="mb-4">
          {!bsaEnabled && isReadOnly
            ? 'Note: BugZero Plan is currently disabled for your organization and your user has read-only access. You will not be able to create or delete projects.'
            : !bsaEnabled
            ? 'Note: BugZero Plan is currently disabled for your organization. You will not be able to create or delete projects.'
            : 'Note: Your user only has read-only access to BugZero Plan. You will not be able to create or delete projects.'}
        </Alert>
      )}
      {loadingProject && (
        <div className="text-center my-8">
          <Spinner color="primary" />
          <p>Loading project data...</p>
        </div>
      )}

      {step === 1 &&
        !loadingProject &&
        (() => {
          // Filter projects by search term
          const filteredProjects = projects.filter(proj =>
            proj.name.toLowerCase().includes(projectsSearchTerm.toLowerCase())
          );
          // Calculate pagination
          const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);
          const startIndex = (projectsCurrentPage - 1) * projectsPerPage;
          const paginatedProjects = filteredProjects.slice(startIndex, startIndex + projectsPerPage);

          return (
            <>
              <div className="bst-projects-container">
                <div className="bst-projects-header">
                  <div className="bst-projects-header-content">
                    <h2>Your Projects</h2>
                    <p>Select an existing project or create a new one to get started</p>
                  </div>
                  {canModifyProjects && (
                    <button type="button" className="bz-btn bz-btn--primary" onClick={handleStartCreateProject}>
                      <span>+</span> New Project
                    </button>
                  )}
                </div>

                {projects.length === 0 ? (
                  <div className="bst-empty-projects">
                    <div className="bst-empty-projects-icon">
                      <FontAwesomeIcon icon={faListAlt} />
                    </div>
                    <h3>No projects yet</h3>
                    <p>Create your first project to start analyzing vendor versions</p>
                    {canModifyProjects && (
                      <button type="button" className="bz-btn bz-btn--primary" onClick={handleStartCreateProject}>
                        <span>+</span> Create Your First Project
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Search and Stats Bar */}
                    <div className="bst-projects-toolbar">
                      <div className="bst-projects-search">
                        <FontAwesomeIcon icon={faSearch} className="bst-search-icon" />
                        <input
                          type="text"
                          placeholder="Search projects..."
                          value={projectsSearchTerm}
                          onChange={e => {
                            setProjectsSearchTerm(e.target.value);
                            setProjectsCurrentPage(1); // Reset to first page on search
                          }}
                          className="bst-search-input"
                        />
                        {projectsSearchTerm && (
                          <button
                            className="bst-search-clear"
                            onClick={() => {
                              setProjectsSearchTerm('');
                              setProjectsCurrentPage(1);
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div className="bst-projects-stats">
                        <span className="bst-stats-text">
                          Showing {paginatedProjects.length} of {filteredProjects.length} project
                          {filteredProjects.length !== 1 ? 's' : ''}
                          {projectsSearchTerm && ` matching "${projectsSearchTerm}"`}
                        </span>
                      </div>
                    </div>

                    {/* Projects Grid */}
                    {paginatedProjects.length === 0 ? (
                      <div className="bst-empty-projects">
                        <div className="bst-empty-projects-icon">
                          <FontAwesomeIcon icon={faSearch} />
                        </div>
                        <h3>No matching projects</h3>
                        <p>Try adjusting your search term</p>
                        <button
                          className="bst-clear-search-btn"
                          onClick={() => {
                            setProjectsSearchTerm('');
                            setProjectsCurrentPage(1);
                          }}
                        >
                          Clear Search
                        </button>
                      </div>
                    ) : (
                      <div className="bst-projects-grid">
                        {paginatedProjects.map(proj => (
                          <div key={proj.id} className="bst-project-card" onClick={() => handleProjectSelection(proj)}>
                            <div className="bst-project-card-content">
                              <div className="bst-project-card-icon">
                                <FontAwesomeIcon icon={faBox} />
                              </div>
                              <div className="bst-project-card-details">
                                <h3>{proj.name}</h3>
                                <span className="bst-project-card-meta">Click to view project details</span>
                              </div>
                            </div>
                            {canModifyProjects && (
                              <button
                                className="bst-project-card-delete"
                                onClick={e => {
                                  e.stopPropagation();
                                  setShowDeleteProject(proj);
                                }}
                                title="Delete project"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="bst-pagination">
                        <button
                          className="bst-pagination-btn"
                          onClick={() => setProjectsCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={projectsCurrentPage === 1}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>

                        <div className="bst-pagination-pages">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              // Show first, last, and pages around current
                              if (page === 1 || page === totalPages) return true;
                              if (Math.abs(page - projectsCurrentPage) <= 1) return true;
                              return false;
                            })
                            .reduce((acc, page, idx, arr) => {
                              // Add ellipsis between non-consecutive pages
                              if (idx > 0 && page - arr[idx - 1] > 1) {
                                acc.push('...');
                              }
                              acc.push(page);
                              return acc;
                            }, [])
                            .map((item, idx) =>
                              item === '...' ? (
                                <span key={`ellipsis-${idx}`} className="bst-pagination-ellipsis">
                                  ...
                                </span>
                              ) : (
                                <button
                                  key={item}
                                  className={`bst-pagination-page ${projectsCurrentPage === item ? 'active' : ''}`}
                                  onClick={() => setProjectsCurrentPage(item)}
                                >
                                  {item}
                                </button>
                              )
                            )}
                        </div>

                        <button
                          className="bst-pagination-btn"
                          onClick={() => setProjectsCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={projectsCurrentPage === totalPages}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          );
        })()}

      {/* Step 1: Vendor, Product, Version Selection */}
      {step === 2 && !loadingProject && (
        <>
          {/* Project Overview Header - Only show when project is selected and not viewing version detail */}
          {selectedProject && !creatingProject && !viewingVersionDetail && (
            <>
              <div className="bst-page-header">
                <div className="bst-project-info">
                  <div className="bst-project-title">
                    <h1>
                      {selectedProject.name}
                      <span className="bst-feature-badge">Project Overview</span>
                    </h1>
                  </div>
                  <div className="bst-project-meta">
                    <span>
                      <FontAwesomeIcon icon={faListAlt} /> Vendor: {vendor?.label}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faListAlt} /> Product: {headerProductLabel}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faClock} /> Current Version: {projectVersion?.label}
                    </span>
                  </div>
                </div>
                <div className="bst-header-actions">
                  <button
                    type="button"
                    className="bz-btn bz-btn--primary bz-btn--sm"
                    onClick={() => setShowExportModal(true)}
                  >
                    Export Report
                  </button>
                  <button
                    type="button"
                    className="bz-btn bz-btn--sm"
                    onClick={() => {
                      setStep(1);
                      setCreatingProject(false);
                      setSelectedProject(null);
                      // A refresh scan may still be running for the project being
                      // exited. Clear its target so the fetch modal unmounts instead
                      // of staying mounted (independent of selectedProject) and
                      // completing against a project that's no longer selected.
                      setRefreshTarget(null);
                      // A prior refresh's error is this project's, not the next
                      // project's — clear it on the way out (STRY0038250 final-review
                      // finding 3).
                      clearRefreshError();
                      // Same for a prior component-toggle error (STRY0038263
                      // final-review finding 3).
                      clearToggleError();
                      navigate('/plan', { replace: false });
                    }}
                  >
                    Exit Project
                  </button>
                </div>
              </div>

              <InventoryDriftAlert
                project={selectedProject}
                onCreateForVersion={() => {
                  setSelectedProject(null);
                  setCreatingProject(true);
                  setScanTarget({
                    id: selectedProject.managedProductId,
                    name: selectedProject.productName || selectedProject.name
                  });
                  // Leave the /plan/:projectId URL so cancelling the scan doesn't
                  // strand the user on a half-filled create form under the old
                  // project's stale URL (STRY0038250 final-review finding 4) - the
                  // same trap d87f9f43 avoided on the CI-deep-link branch.
                  navigate('/plan', { replace: false });
                }}
              />

              {/* Stats Row */}
              <div className="bst-stats-row">
                <InventoryFleetStats project={selectedProject} />
                <div className="bst-stat-card highlight">
                  <div className="bst-stat-label">Versions Available</div>
                  <div className="bst-stat-value">{upgradeVersions.length}</div>
                  <div className="bst-stat-subtext">{selectedProject?.versions?.length || 0} tracked in project</div>
                </div>
                <div className="bst-stat-card">
                  <div className="bst-stat-label">Review Progress</div>
                  <div className="bst-stat-value">{getReviewProgress().percentage}%</div>
                  <div className="bst-progress-bar">
                    <div className="bst-progress-fill" style={{ width: `${getReviewProgress().percentage}%` }} />
                  </div>
                  <div className="bst-stat-subtext">
                    {getReviewProgress().reviewed} of {getReviewProgress().total} versions reviewed
                  </div>
                </div>
                <div className={`bst-stat-card ${getAcceptedVersion() ? 'accepted' : 'pending'}`}>
                  <div className="bst-stat-label">Accepted Version</div>
                  <div className="bst-stat-value">
                    {getAcceptedVersion() ? (
                      <span className="bst-accepted-version">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        {getAcceptedVersion()}
                      </span>
                    ) : (
                      <span className="bst-pending-version">—</span>
                    )}
                  </div>
                  <div className="bst-stat-subtext">
                    {getAcceptedVersion() ? 'Target upgrade version' : 'No version accepted yet'}
                  </div>
                </div>
              </div>

              <ComponentProfilePanel
                project={selectedProject}
                saving={savingComponent}
                toggleError={toggleError}
                refreshing={refreshing}
                refreshError={refreshError}
                onRefresh={() =>
                  setRefreshTarget({
                    id: selectedProject.managedProductId,
                    name: selectedProject.productName || selectedProject.name
                  })
                }
                onToggle={async (component, enabled) => {
                  const overrides = await applyToggle(selectedProject.id, component, enabled);
                  // null means the save failed; leave the panel as it was.
                  if (overrides) {
                    setSelectedProject(prev => (prev ? { ...prev, componentOverrides: overrides } : prev));
                  }
                }}
              />
            </>
          )}

          {/* Create New Project Form - Only show when creating */}
          {creatingProject && (
            <div className="bst-create-project-container">
              {/* Header */}
              <div className="bst-create-project-header">
                <div className="bst-create-project-title">
                  <div className="bst-create-project-icon">
                    <FontAwesomeIcon icon={faPlus} />
                  </div>
                  <div>
                    <h2>Create New Project</h2>
                    <p>Set up a new project to track software versions and vulnerabilities</p>
                  </div>
                </div>
                <button
                  className="bst-create-project-cancel"
                  onClick={() => {
                    setStep(1);
                    setCreatingProject(false);
                    navigate('/plan', { replace: false });
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Cancel
                </button>
              </div>

              {/* Progress Steps */}
              <div className="bst-create-project-steps">
                <div className={`bst-step ${vendor ? 'completed' : 'active'}`}>
                  <div className="bst-step-number">1</div>
                  <div className="bst-step-label">Select Vendor</div>
                </div>
                <div className="bst-step-connector" />
                <div className={`bst-step ${product ? 'completed' : vendor ? 'active' : ''}`}>
                  <div className="bst-step-number">2</div>
                  <div className="bst-step-label">Choose Product</div>
                </div>
                <div className="bst-step-connector" />
                <div className={`bst-step ${version ? 'completed' : product ? 'active' : ''}`}>
                  <div className="bst-step-number">3</div>
                  <div className="bst-step-label">Select Version</div>
                </div>
                <div className="bst-step-connector" />
                <div
                  className={`bst-step ${
                    projectName && projectName.trim().length >= 3 ? 'completed' : version ? 'active' : ''
                  }`}
                >
                  <div className="bst-step-number">4</div>
                  <div className="bst-step-label">Name Project</div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="bst-create-project-form">
                <Row>
                  {/* Vendor Selection */}
                  <Col md="6" lg="3">
                    <div className="bst-form-field">
                      <div className="bst-form-field-header">
                        <FontAwesomeIcon icon={faBuilding} className="field-icon" />
                        <label>Vendor</label>
                        <span className="required">*</span>
                      </div>
                      <Select
                        options={loadingVendors ? [] : vendors}
                        value={vendor}
                        isLoading={loadingVendors}
                        isDisabled={loadingVendors}
                        placeholder={loadingVendors ? 'Loading vendors...' : 'Select a vendor'}
                        styles={{
                          control: base => ({
                            ...base,
                            minHeight: '44px',
                            borderRadius: '8px',
                            borderColor: 'var(--line)'
                          })
                        }}
                        components={{
                          LoadingIndicator: () => <Spinner size="sm" color="secondary" />
                        }}
                        onChange={selectedVendor => {
                          setLoadingProducts(true);
                          setVendor(selectedVendor);
                          fetchProducts(selectedVendor);
                        }}
                      />
                    </div>
                  </Col>

                  {/* Product Selection */}
                  <Col md="6" lg="5">
                    <div className={`bst-form-field ${!vendor ? 'disabled' : ''}`}>
                      <div className="bst-form-field-header">
                        <FontAwesomeIcon icon={faBox} className="field-icon" />
                        <label>Product</label>
                        <span className="required">*</span>
                      </div>
                      <Select
                        options={
                          loadingProducts
                            ? []
                            : // A read-only user or a Plan-disabled tenant cannot create a project, so
                              // selecting a managed product here — which would kick off a live CMDB scan
                              // — is never offered; only the manual "All products" list is.
                              buildProductGroups(canModifyProjects ? managedProducts : [], products)
                        }
                        value={product}
                        isLoading={loadingProducts}
                        isDisabled={!vendor || loadingProducts}
                        onChange={selectedProduct => {
                          if (selectedProduct && selectedProduct.managedProductId) {
                            if (!canModifyProjects) return;
                            setProduct(null);
                            setVersion(null);
                            setScanTarget({
                              id: selectedProduct.managedProductId,
                              name: selectedProduct.label
                            });
                            return;
                          }
                          setLoadingVersions(true);
                          setProduct(selectedProduct);
                          fetchVersions(selectedProduct, vendor);
                        }}
                        filterOption={filterByValueOption}
                        placeholder={
                          !vendor
                            ? 'Select a vendor first'
                            : loadingProducts
                            ? 'Loading products...'
                            : 'Search and select a product'
                        }
                        styles={{
                          control: base => ({
                            ...base,
                            minHeight: '44px',
                            borderRadius: '8px',
                            borderColor: 'var(--line)'
                          })
                        }}
                        components={{
                          LoadingIndicator: () => <Spinner size="sm" color="secondary" />
                        }}
                      />
                    </div>
                  </Col>

                  {/* Version Selection */}
                  <Col md="6" lg="4">
                    <div className={`bst-form-field ${!product ? 'disabled' : ''}`}>
                      <div className="bst-form-field-header">
                        <FontAwesomeIcon icon={faCodeBranch} className="field-icon" />
                        <label>Current Version</label>
                        <span className="required">*</span>
                      </div>
                      <Select
                        options={loadingVersions ? [] : versions}
                        value={version}
                        isLoading={loadingVersions}
                        isDisabled={!product || loadingVersions}
                        onChange={selectedVersion => {
                          setLoadingUpgradeVersions(true);
                          setOpenAccordion('');
                          setVersion(selectedVersion);
                          setProjectName(`${vendor.label} - ${product.label} - ${selectedVersion.label}`);
                          fetchUpgradeVersions(selectedVersion, vendor, product);
                        }}
                        placeholder={
                          !product
                            ? 'Select a product first'
                            : loadingVersions
                            ? 'Loading versions...'
                            : 'Select current version'
                        }
                        styles={{
                          control: base => ({
                            ...base,
                            minHeight: '44px',
                            borderRadius: '8px',
                            borderColor: 'var(--line)'
                          })
                        }}
                        components={{
                          LoadingIndicator: () => <Spinner size="sm" color="secondary" />
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Project Name - Only show after version is selected */}
                {version && (
                  <div className="bst-project-name-section">
                    <div className="bst-form-field">
                      <div className="bst-form-field-header">
                        <FontAwesomeIcon icon={faTag} className="field-icon" />
                        <label>Project Name</label>
                        <span className="required">*</span>
                      </div>
                      <input
                        type="text"
                        className="bst-project-name-input"
                        value={projectName}
                        onChange={e => setProjectName(e.target.value)}
                        placeholder="Enter a descriptive name for your project"
                        maxLength={128}
                      />
                      <div className="bst-form-field-hint">
                        {projectName.length}/128 characters
                        {projectName.trim().length < 3 && projectName.length > 0 && (
                          <span className="error"> - Minimum 3 characters required</span>
                        )}
                      </div>
                    </div>

                    {/* Create Button */}
                    <div className="bst-create-project-actions">
                      <button
                        type="button"
                        className="bz-btn bz-btn--primary"
                        onClick={handleCreateProject}
                        disabled={!projectName || !canModifyProjects || projectName.trim().length < 3 || savingProject}
                      >
                        {savingProject ? (
                          <>
                            <Spinner size="sm" /> Creating Project...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faPlus} /> Create Project
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {scanTarget && !scanResult && (
            <InventoryFetchModal
              isOpen
              managedProductId={scanTarget.id}
              managedProductName={scanTarget.name}
              onComplete={result => setScanResult(result)}
              onCancel={handleInventoryCancel}
            />
          )}
          {scanTarget && scanResult && (
            <InventoryConfirmStep
              snapshot={scanResult}
              managedProductId={scanTarget.id}
              managedProductName={scanTarget.name}
              vendorId={vendor?.value}
              allProducts={products}
              onCreated={project => {
                setScanTarget(null);
                setScanResult(null);
                setProjects(current => [...current, project]);
                handleProjectSelection(project);
                // Mirrors the manual create flow (handleCreateProject): without this the
                // create form stays mounted and the project header/version list stay hidden.
                setCreatingProject(false);
              }}
              onCancel={handleInventoryCancel}
            />
          )}
          {refreshTarget && (
            <InventoryFetchModal
              isOpen
              managedProductId={refreshTarget.id}
              managedProductName={refreshTarget.name}
              onComplete={async result => {
                // The user may have exited the project (or the modal's completion
                // callback may already be in flight) while the scan was still
                // running. If there's no selected project left to apply the
                // refresh to, discard the result quietly instead of calling
                // applyRefresh against a null project.
                if (!selectedProject) {
                  setRefreshTarget(null);
                  return;
                }
                const updated = await applyRefresh(selectedProject.id, result.fetchJobId);
                setRefreshTarget(null);
                // On failure `updated` is null and we keep the snapshot the
                // user already had; the panel shows the error.
                //
                // The refresh endpoint returns only {id, inventorySnapshot,
                // componentOverrides} — never the full project GET
                // /bsa/project/{id} returns. Merging onto the freshest
                // selectedProject (via the updater form, not the closed-over
                // variable) preserves name/versionId/versions/productName/
                // managedProductId etc.; replacing wholesale blanked the
                // header, zeroed review progress, and forced versionId to
                // undefined — which permanently disabled the drift banner
                // (STRY0038250 final-review finding 1).
                if (updated) {
                  setSelectedProject(prev => (prev ? { ...prev, ...updated } : updated));
                }
              }}
              onCancel={() => setRefreshTarget(null)}
            />
          )}
        </>
      )}

      {/* Version List Table - Hidden when viewing version detail */}
      {isStep1Complete && !creatingProject && step !== 1 && !loadingProject && !viewingVersionDetail && (
        <>
          {/* Version List */}
          <div className="bst-version-list">
            <div className="bst-version-list-header">
              <h3>Available Versions</h3>
              <span style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
                {upgradeVersions.length} version{upgradeVersions.length !== 1 ? 's' : ''} available
              </span>
            </div>

            {loadingUpgradeVersions && (
              <div className="bst-empty-state">
                <Spinner color="primary" />
                <p>Loading version data...</p>
              </div>
            )}

            {upgradeVersions.length === 0 && !loadingUpgradeVersions && (
              <div className="bst-empty-state">
                <div className="bst-empty-state-icon">
                  <FontAwesomeIcon icon={faListAlt} />
                </div>
                <p>No compatible versions found.</p>
              </div>
            )}

            {!!upgradeVersions.length && !loadingUpgradeVersions && (
              <table className="bst-version-table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Bugs</th>
                    <th>CVEs</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {upgradeVersions.map((uv, index) => {
                    const state = getVersionState(uv.suggestionId);
                    return (
                      <tr key={index} onClick={() => handleVersionClick(uv, index)}>
                        <td>
                          <div className="bst-version-info">
                            <span className="bst-version-number">
                              {uv.releaseVersion}
                              {uv.isSuggested && <span className="bst-suggested-tag">Suggested</span>}
                            </span>
                            <span className="bst-version-date">
                              {uv.releaseDate ? moment.utc(uv.releaseDate).format('MMM D, YYYY') : 'No date'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`bst-status-badge ${getStatusBadgeClass(state)}`}>
                            {getStatusSymbol(state)} {state}
                          </span>
                        </td>
                        <td className="bst-count-cell">
                          {comparisonDataCache[uv.suggestionId] ? (
                            <span className="bst-cached-metric">
                              <span className="bst-metric-count">
                                {comparisonDataCache[uv.suggestionId].bugs?.length || 0}
                              </span>
                              {comparisonDataCache[uv.suggestionId].bugAverage && (
                                <span
                                  className={`bst-metric-score ${getRiskClass(
                                    comparisonDataCache[uv.suggestionId].bugAverage
                                  )}`}
                                >
                                  {comparisonDataCache[uv.suggestionId].bugAverage}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="bst-uncached">-</span>
                          )}
                        </td>
                        <td className="bst-count-cell">
                          {comparisonDataCache[uv.suggestionId] ? (
                            <span className="bst-cached-metric">
                              <span className="bst-metric-count">
                                {comparisonDataCache[uv.suggestionId].cves?.length || 0}
                              </span>
                              {comparisonDataCache[uv.suggestionId].cveAverage && (
                                <span
                                  className={`bst-metric-score ${getRiskClass(
                                    comparisonDataCache[uv.suggestionId].cveAverage
                                  )}`}
                                >
                                  {comparisonDataCache[uv.suggestionId].cveAverage}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="bst-uncached">-</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{ color: 'var(--info)', fontSize: '0.85rem', fontWeight: 500 }}>
                            View details →
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Version Detail View - Full screen when viewing a version */}
      {isStep1Complete && !creatingProject && step !== 1 && !loadingProject && viewingVersionDetail && (
        <>
          {/* Project Header for Version Detail */}
          <div className="bst-page-header bst-version-detail-project-header">
            <div className="bst-project-info">
              <div className="bst-project-title">
                <h1>
                  {/* The project's own name, not a title recomposed from
                      vendor + ODD product + version — that spelled the module
                      out ("F5 - BIG-IP LTM - 17.1.1") on a project about a
                      device, and disagreed with the name shown everywhere
                      else. Falls back to the composed form for a project
                      somehow lacking a name. */}
                  {selectedProject?.name || `${vendor?.label} - ${product?.label} - ${projectVersion?.label}`}
                  <span className="bst-feature-badge">Project Overview</span>
                </h1>
              </div>
              <div className="bst-project-meta">
                <span>
                  <FontAwesomeIcon icon={faListAlt} /> Vendor: {vendor?.label}
                </span>
                <span>
                  <FontAwesomeIcon icon={faListAlt} /> Product: {headerProductLabel}
                </span>
                <span>
                  <FontAwesomeIcon icon={faClock} /> Current Version: {projectVersion?.label}
                </span>
              </div>
            </div>
          </div>

          {/* Loading state */}
          {loadingAccordion && (
            <div className="bst-version-detail">
              <div className="bst-version-detail-header" style={{ justifyContent: 'flex-start' }}>
                <button className="bst-back-btn" onClick={handleBackToList}>
                  ← Back to versions
                </button>
              </div>
              <div className="bst-loading-state">
                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                <h4>Loading version details...</h4>
                <p>Fetching bugs, CVEs, and review status</p>
              </div>
            </div>
          )}

          {/* Version Detail Panel */}
          {selectedVersion && !loadingAccordion && (
            <div className="bst-version-detail">
              <div className="bst-version-detail-header">
                <div className="bst-version-detail-title">
                  <button className="bst-back-btn" onClick={handleBackToList}>
                    ← Back to versions
                  </button>
                  <h3>Version {selectedVersion.releaseVersion}</h3>
                  {selectedVersion.isSuggested && <span className="bst-suggested-tag">Vendor Suggested</span>}
                </div>
                <div className="bst-version-detail-meta">
                  <span>
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    {selectedVersion.releaseDate
                      ? `Released ${moment.utc(selectedVersion.releaseDate).format('MMM D, YYYY')}`
                      : 'No release date'}
                  </span>
                  <span>
                    Age: {selectedVersion.releaseDate ? moment.utc(selectedVersion.releaseDate).fromNow() : '-'}
                  </span>
                </div>
              </div>

              <div className="bst-version-detail-body">
                {/* Risk Summary Cards */}
                <div className="bst-risk-summary">
                  <div className="bst-risk-card operational">
                    <div className="bst-risk-card-icon">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="bst-risk-card-label">Operational Risk</div>
                    <div className={`bst-risk-card-value ${getRiskClass(bugAverage)}`}>{bugAverage || '-'}</div>
                    <div className="bst-risk-card-subtext">{getRiskDetails(bugAverage).label}</div>
                  </div>
                  <div className="bst-risk-card security">
                    <div className="bst-risk-card-icon">
                      <FontAwesomeIcon icon={faShieldAlt} />
                    </div>
                    <div className="bst-risk-card-label">Security Risk</div>
                    <div className={`bst-risk-card-value ${getRiskClass(cveAverage)}`}>{cveAverage || '-'}</div>
                    <div className="bst-risk-card-subtext">{getRiskDetails(cveAverage).label}</div>
                  </div>
                  <div className="bst-risk-card count">
                    <div className="bst-risk-card-icon">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="bst-risk-card-label">Total Bugs</div>
                    <div className="bst-risk-card-value">{bugs.length}</div>
                    <div className="bst-risk-card-subtext">{getFilteredBugs().length} after filters</div>
                  </div>
                  <div className="bst-risk-card count">
                    <div className="bst-risk-card-icon">
                      <FontAwesomeIcon icon={faShieldAlt} />
                    </div>
                    <div className="bst-risk-card-label">Total CVEs</div>
                    <div className="bst-risk-card-value">{cves.length}</div>
                    <div className="bst-risk-card-subtext">{getFilteredCves().length} after filters</div>
                  </div>
                </div>

                {/* Risk Distribution Row */}
                {(bugs.length > 0 || cves.length > 0) && (
                  <div className="bst-distribution-row">
                    {/* Bug Severity Distribution */}
                    <div className="bst-distribution-card">
                      <div className="bst-distribution-header">
                        <span className="bst-distribution-icon bugs">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </span>
                        <div className="bst-distribution-title">
                          <h4>Bug Severity Distribution</h4>
                          <span className="bst-distribution-total">
                            {getBugSeverityDistribution().total} total bugs
                          </span>
                        </div>
                      </div>
                      <div className="bst-distribution-bars">
                        {(() => {
                          const { distribution, total } = getBugSeverityDistribution();
                          return distribution.map((item, index) => {
                            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                            return (
                              <div key={item.severity} className="bst-distribution-item">
                                <span className="bst-distribution-label">{item.severity}</span>
                                <div className="bst-distribution-bar-container">
                                  <div
                                    className="bst-distribution-bar-fill"
                                    style={{
                                      width: `${percentage}%`,
                                      background: getDistributionBarColor(index, distribution.length)
                                    }}
                                  >
                                    {percentage >= 10 && (
                                      <span className="bst-distribution-bar-text">{item.count}</span>
                                    )}
                                  </div>
                                </div>
                                <span className="bst-distribution-count">{item.count}</span>
                              </div>
                            );
                          });
                        })()}
                        {getBugSeverityDistribution().total === 0 && (
                          <div className="bst-distribution-empty">No bugs for this version</div>
                        )}
                      </div>
                    </div>

                    {/* CVE Severity Distribution */}
                    <div className="bst-distribution-card">
                      <div className="bst-distribution-header">
                        <span className="bst-distribution-icon cves">
                          <FontAwesomeIcon icon={faShieldAlt} />
                        </span>
                        <div className="bst-distribution-title">
                          <h4>CVE Severity Distribution</h4>
                          <span className="bst-distribution-total">
                            {getCveSeverityDistribution().total} total CVEs
                          </span>
                        </div>
                      </div>
                      <div className="bst-distribution-bars">
                        {(() => {
                          const { distribution, total } = getCveSeverityDistribution();
                          return distribution.map((item, index) => {
                            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                            return (
                              <div key={item.severity} className="bst-distribution-item">
                                <span className="bst-distribution-label">{item.severity}</span>
                                <div className="bst-distribution-bar-container">
                                  <div
                                    className="bst-distribution-bar-fill"
                                    style={{
                                      width: `${percentage}%`,
                                      background: getDistributionBarColor(index, distribution.length)
                                    }}
                                  >
                                    {percentage >= 10 && (
                                      <span className="bst-distribution-bar-text">{item.count}</span>
                                    )}
                                  </div>
                                </div>
                                <span className="bst-distribution-count">{item.count}</span>
                              </div>
                            );
                          });
                        })()}
                        {getCveSeverityDistribution().total === 0 && (
                          <div className="bst-distribution-empty">No CVEs for this version</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Feature Tabs */}
                <div className="bst-feature-tabs">
                  <button
                    className={`bst-feature-tab ${activeDetailTab === 'review' ? 'active' : ''}`}
                    onClick={() => setActiveDetailTab('review')}
                  >
                    <FontAwesomeIcon icon={faListAlt} className="tab-icon" />
                    Version Review
                  </button>
                  <button
                    className={`bst-feature-tab ${activeDetailTab === 'comparison' ? 'active' : ''}`}
                    onClick={async () => {
                      setActiveDetailTab('comparison');
                      // Pre-populate with the selected version as first comparison
                      if (selectedVersion && !comparisonVersions[0]) {
                        setComparisonVersions([selectedVersion, null]);
                        // Fetch comparison data for the pre-populated version
                        await fetchComparisonVersionData(selectedVersion, 0);
                      } else if (comparisonVersions[0] && !comparisonDataCache[comparisonVersions[0].suggestionId]) {
                        // Fetch data for existing comparison version if not cached
                        await fetchComparisonVersionData(comparisonVersions[0], 0);
                      }
                      // Also fetch data for second comparison version if it exists and isn't cached
                      if (comparisonVersions[1] && !comparisonDataCache[comparisonVersions[1].suggestionId]) {
                        await fetchComparisonVersionData(comparisonVersions[1], 1);
                      }
                      // Fetch current version data if not loaded
                      await fetchCurrentVersionData();
                    }}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="tab-icon" />
                    Compare Versions
                  </button>
                </div>

                {/* Version Review Tab Content */}
                {activeDetailTab === 'review' && (
                  <>
                    {/* Review State Section */}
                    <div className="bst-review-state-card">
                      <div className="bst-review-state-header">
                        <div className="bst-review-state-icon">
                          <FontAwesomeIcon icon={faListAlt} />
                        </div>
                        <div className="bst-review-state-info">
                          <h4 className="bst-review-state-title">Version Review State</h4>
                          <p className="bst-review-state-desc">
                            Set the review status for this version to track your evaluation progress
                          </p>
                        </div>
                      </div>
                      <div className="bst-review-state-selector">
                        <Select
                          options={reviewStateOptions}
                          value={
                            userVersionData?.state
                              ? { value: userVersionData?.state, label: userVersionData?.state }
                              : reviewStateOptions[0]
                          }
                          onChange={async e => {
                            setSavingVersionState(true);
                            await handleSetVersionState({
                              state: e.value,
                              versionId: selectedVersion.suggestionId,
                              projectId: selectedProject.id,
                              productName: selectedProject.productName,
                              productId: selectedProject.productId
                            });
                            setSavingVersionState(false);
                          }}
                          isDisabled={savingVersionState}
                          styles={{
                            control: base => ({
                              ...base,
                              minHeight: '44px',
                              borderRadius: '8px',
                              borderColor: 'var(--line)'
                            })
                          }}
                        />
                        {savingVersionState && (
                          <div className="bst-review-state-saving">
                            <Spinner size="sm" color="primary" />
                            <span>Saving...</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Comments Section */}
                    <Comments
                      comments={userVersionData?.comments || []}
                      setUserVersionData={setUserVersionData}
                      userVersionData={userVersionData}
                      fetchUserVersionState={fetchUserVersionState}
                      selectedVersion={selectedVersion}
                      projectId={selectedProject.id}
                      versionId={selectedVersion.suggestionId}
                      productName={selectedProject.productName}
                      productId={selectedProject.productId}
                    />
                    {/* Bugs & CVEs Tabbed Section */}
                    <div className="bst-data-tabs-container">
                      {/* Tab Headers */}
                      <div className="bst-data-tabs">
                        <button
                          className={`bst-data-tab ${activeDataTab === 'bugs' ? 'active' : ''}`}
                          onClick={() => setActiveDataTab('bugs')}
                        >
                          <FontAwesomeIcon icon={faExclamationTriangle} className="tab-icon" />
                          Operational Bugs
                          <span className="bst-tab-count">{bugs?.length || 0}</span>
                        </button>
                        <button
                          className={`bst-data-tab ${activeDataTab === 'cves' ? 'active' : ''}`}
                          onClick={() => setActiveDataTab('cves')}
                        >
                          <FontAwesomeIcon icon={faShieldAlt} className="tab-icon" />
                          Security CVEs
                          <span className="bst-tab-count">{cves?.length || 0}</span>
                        </button>
                      </div>

                      {/* Bugs Tab Content */}
                      {activeDataTab === 'bugs' && (
                        <div className="bst-data-tab-content">
                          {/* Search and Filter Bar */}
                          <div className="bst-data-toolbar">
                            <div className="bst-search-box">
                              <FontAwesomeIcon icon={faSearch} className="search-icon" />
                              <input
                                type="text"
                                placeholder="Search bugs by ID, summary, or severity..."
                                value={bugSearchTerm}
                                onChange={e => {
                                  setBugSearchTerm(e.target.value);
                                  setBugCurrentPage(1);
                                }}
                              />
                              {bugSearchTerm && (
                                <button className="clear-search" onClick={() => setBugSearchTerm('')}>
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              )}
                            </div>
                            <div className="bst-results-info">
                              Showing {getPaginatedBugs().length} of {getFilteredBugs().length} bugs
                            </div>
                          </div>

                          {/* Filters */}
                          {!!bugFilterMeta?.length && (
                            <div className="bst-filters-card">
                              <div className="bst-filters-header">
                                <div className="bst-filters-icon">
                                  <FontAwesomeIcon icon={faFilter} />
                                </div>
                                <div className="bst-filters-info">
                                  <h5 className="bst-filters-title">Filter Bugs</h5>
                                  <p className="bst-filters-desc">Narrow down results by selecting filter criteria</p>
                                </div>
                              </div>
                              <div className="bst-filters-grid">
                                {bugFilterMeta.map((filter, index) => (
                                  <div key={index} className="bst-filter-field">
                                    <label className="bst-filter-label">{filter.label}</label>
                                    <Select
                                      isMulti
                                      placeholder={`All`}
                                      styles={{
                                        control: base => ({
                                          ...base,
                                          minHeight: '42px',
                                          borderRadius: '8px',
                                          borderColor: 'var(--line)',
                                          boxShadow: 'none',
                                          '&:hover': {
                                            borderColor: 'var(--line-strong)'
                                          }
                                        }),
                                        multiValue: base => ({
                                          ...base,
                                          backgroundColor: 'var(--info-soft)',
                                          borderRadius: '6px'
                                        }),
                                        multiValueLabel: base => ({
                                          ...base,
                                          color: 'var(--info)',
                                          fontWeight: 500
                                        }),
                                        multiValueRemove: base => ({
                                          ...base,
                                          color: 'var(--info)',
                                          '&:hover': {
                                            backgroundColor: 'var(--info-soft)',
                                            color: 'var(--info)'
                                          }
                                        })
                                      }}
                                      options={getUniqueBugOptions(filter.key)}
                                      value={(bugFilters[filter.key] || []).map(value => ({
                                        value,
                                        label: value
                                      }))}
                                      onChange={selected => {
                                        setBugFilters({
                                          ...bugFilters,
                                          [filter.key]: selected ? selected.map(item => item.value) : []
                                        });
                                        setBugCurrentPage(1);
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {overlayError && (
                            <Alert color="warning" className="mb-2">
                              {overlayError}
                            </Alert>
                          )}

                          {overrideError && (
                            <Alert color="danger" className="mb-2" role="alert">
                              {overrideError}
                            </Alert>
                          )}

                          {/* Bug Table */}
                          <Table bordered hover className="bst-data">
                            <thead>
                              <tr>
                                <th>Bug ID</th>
                                <th>Summary</th>
                                <th>Severity</th>
                                <th>BugZero Risk Score</th>
                                {showComponentColumn && <ComponentColumnHeader id="bst-component-col-bugs" />}
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getPaginatedBugs().length > 0 ? (
                                getPaginatedBugs().map((bug, index) => {
                                  // Computed once per row: the same merged state drives the
                                  // strikethrough, the Component cell and the status control,
                                  // so they cannot disagree about what this issue is.
                                  const mergedState = effectiveState(bug.id, bugStates, overlay?.bugs);
                                  const strike = isStruckOut(mergedState.value) ? 'text-decoration-line-through' : '';
                                  const componentData = showComponentColumn
                                    ? componentCellData(bug?.affected_components, {
                                        isAuto: mergedState.isAuto,
                                        hint: partialHintFor(bug.id, overlay?.partialHints?.bugs),
                                        configuredCiCount: overlay?.configuredCiCount
                                      })
                                    : null;
                                  return (
                                    <tr id={index} key={index}>
                                      <td>
                                        <a href={bug?.vendor_url} target="_blank" rel="noopener noreferrer">
                                          <span className={strike}>{bug.id}</span>
                                        </a>
                                      </td>
                                      <td>
                                        <span className={strike}>{bug?.title || '-'}</span>
                                      </td>
                                      <td>
                                        <span className={strike}>{bug?.severity || '-'}</span>
                                      </td>
                                      <td>
                                        {bug?.bugRiskScore ? (
                                          <a
                                            href={`${ODD.portalUrl}/operational-defect-database/vendors/${bug.vendor_id}/defects/${bug.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <span className={strike}>{bug?.bugRiskScore}</span>
                                          </a>
                                        ) : (
                                          '-'
                                        )}
                                      </td>
                                      {showComponentColumn && (
                                        <ComponentCell data={componentData} tooltipId={`bst-comp-bug-${bug.id}`} />
                                      )}
                                      <td>
                                        <button
                                          className="bst-comment-link"
                                          onClick={e => {
                                            e.preventDefault();
                                            setShowBugComment(bug.id);
                                          }}
                                        >
                                          {userVersionData?.bugs?.find(item => item.bugId === bug.id)?.comments
                                            ?.length || 'Add'}{' '}
                                          {userVersionData?.bugs?.find(item => item.bugId === bug.id)?.comments
                                            ?.length === 1
                                            ? 'comment'
                                            : 'comments'}
                                        </button>
                                        <br />
                                        {bugStates[bug.id]?.edit ? (
                                          <>
                                            <Select
                                              styles={{
                                                control: provided => ({
                                                  ...provided,
                                                  width: 'auto',
                                                  minWidth: 'fit-content',
                                                  whiteSpace: 'nowrap',
                                                  maxWidth: '100%'
                                                }),
                                                menu: provided => ({
                                                  ...provided,
                                                  whiteSpace: 'nowrap',
                                                  width: 'auto'
                                                })
                                              }}
                                              isDisabled={savingBugCve}
                                              options={BUG_CVE_REVIEW_STATES}
                                              value={{ value: mergedState.value, label: mergedState.value }}
                                              onChange={async e => {
                                                setSavingBugCve(true);
                                                setOverrideError(null);
                                                let projectVersionId;
                                                try {
                                                  projectVersionId = await ensureVersionRow({
                                                    project: selectedProject,
                                                    version: selectedVersion,
                                                    userVersionData,
                                                    setVersionState: BZAPIService.bsaSetVersionState
                                                  });
                                                } catch (err) {
                                                  // Leave the issue as it was — auto-marked — rather
                                                  // than showing a state we failed to store.
                                                  setSavingBugCve(false);
                                                  setOverrideError('Could not save that change. Please try again.');
                                                  return;
                                                }
                                                await BZAPIService.bsaSetVersionBugCveState({
                                                  projectVersionId,
                                                  productName: selectedProject.productName,
                                                  productId: selectedProject.productId,
                                                  vendorId: selectedProject.vendorId,
                                                  isCve: false,
                                                  bugId: bug.id,
                                                  state: e.value
                                                });
                                                await fetchUserVersionState(selectedVersion);
                                                setSavingBugCve(false);
                                              }}
                                            />
                                            {savingBugCve && (
                                              <div className="my-2 text-center">
                                                <Spinner color="primary" />
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <button
                                            className="bst-status-link"
                                            onClick={e => {
                                              e.preventDefault();
                                              // Finding 2 (STRY0038263 final-review): seed the
                                              // synthetic edit entry from the merged state the
                                              // reviewer is actually looking at, not a hardcoded
                                              // 'Not reviewed' — otherwise opening the control
                                              // silently throws away the auto-mark verdict
                                              // effectiveState was just showing. The auto flavour
                                              // is never a selectable option, so its human
                                              // equivalent (CONFIG_REMOVED) is seeded instead.
                                              const seedValue =
                                                mergedState.value === AUTO_CONFIG_REMOVED
                                                  ? CONFIG_REMOVED
                                                  : mergedState.value;
                                              setBugStates({
                                                ...bugStates,
                                                [bug.id]: {
                                                  value: seedValue,
                                                  edit: true
                                                }
                                              });
                                            }}
                                          >
                                            {/* The partial-enablement hint used to live here as a
                                              second line, which grew the Status cell and shifted
                                              the row above it. It belongs to the Component column
                                              now — same information, no layout shift. */}
                                            <span
                                              className={mergedState.isAuto ? 'bst-state-auto' : undefined}
                                              title={
                                                mergedState.isAuto
                                                  ? `No device runs ${mergedState.components.join(', ')}`
                                                  : undefined
                                              }
                                            >
                                              {mergedState.value}
                                            </span>
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={showComponentColumn ? '6' : '5'} className="text-center">
                                    {bugSearchTerm ? 'No bugs match your search.' : 'No bugs found.'}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>

                          {/* Pagination */}
                          {getBugTotalPages() > 1 && (
                            <div className="bst-pagination">
                              <button
                                className="bst-pagination-btn"
                                onClick={() => setBugCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={bugCurrentPage === 1}
                              >
                                <FontAwesomeIcon icon={faChevronLeft} />
                              </button>
                              <div className="bst-pagination-pages">
                                {Array.from({ length: getBugTotalPages() }, (_, i) => i + 1)
                                  .filter(page => {
                                    if (page === 1 || page === getBugTotalPages()) return true;
                                    if (Math.abs(page - bugCurrentPage) <= 1) return true;
                                    return false;
                                  })
                                  .reduce((acc, page, idx, arr) => {
                                    if (idx > 0 && page - arr[idx - 1] > 1) {
                                      acc.push('...');
                                    }
                                    acc.push(page);
                                    return acc;
                                  }, [])
                                  .map((item, idx) =>
                                    item === '...' ? (
                                      <span key={`ellipsis-${idx}`} className="bst-pagination-ellipsis">
                                        ...
                                      </span>
                                    ) : (
                                      <button
                                        key={item}
                                        className={`bst-pagination-page ${bugCurrentPage === item ? 'active' : ''}`}
                                        onClick={() => setBugCurrentPage(item)}
                                      >
                                        {item}
                                      </button>
                                    )
                                  )}
                              </div>
                              <button
                                className="bst-pagination-btn"
                                onClick={() => setBugCurrentPage(prev => Math.min(prev + 1, getBugTotalPages()))}
                                disabled={bugCurrentPage === getBugTotalPages()}
                              >
                                <FontAwesomeIcon icon={faChevronRight} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* CVEs Tab Content */}
                      {activeDataTab === 'cves' && (
                        <div className="bst-data-tab-content">
                          {/* Search and Filter Bar */}
                          <div className="bst-data-toolbar">
                            <div className="bst-search-box">
                              <FontAwesomeIcon icon={faSearch} className="search-icon" />
                              <input
                                type="text"
                                placeholder="Search CVEs by ID, summary, or severity..."
                                value={cveSearchTerm}
                                onChange={e => {
                                  setCveSearchTerm(e.target.value);
                                  setCveCurrentPage(1);
                                }}
                              />
                              {cveSearchTerm && (
                                <button className="clear-search" onClick={() => setCveSearchTerm('')}>
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              )}
                            </div>
                            <div className="bst-results-info">
                              Showing {getPaginatedCves().length} of {getFilteredCves().length} CVEs
                            </div>
                          </div>

                          {/* Filters */}
                          {!!cveFilterMeta?.length && (
                            <div className="bst-filters-card">
                              <div className="bst-filters-header">
                                <div className="bst-filters-icon cve">
                                  <FontAwesomeIcon icon={faFilter} />
                                </div>
                                <div className="bst-filters-info">
                                  <h5 className="bst-filters-title">Filter CVEs</h5>
                                  <p className="bst-filters-desc">Narrow down results by selecting filter criteria</p>
                                </div>
                              </div>
                              <div className="bst-filters-grid">
                                {cveFilterMeta.map((filter, index) => (
                                  <div key={index} className="bst-filter-field">
                                    <label className="bst-filter-label">{filter.label}</label>
                                    <Select
                                      isMulti
                                      placeholder={`All`}
                                      styles={{
                                        control: base => ({
                                          ...base,
                                          minHeight: '42px',
                                          borderRadius: '8px',
                                          borderColor: 'var(--line)',
                                          boxShadow: 'none',
                                          '&:hover': {
                                            borderColor: 'var(--line-strong)'
                                          }
                                        }),
                                        multiValue: base => ({
                                          ...base,
                                          backgroundColor: 'var(--predict-soft)',
                                          borderRadius: '6px'
                                        }),
                                        multiValueLabel: base => ({
                                          ...base,
                                          color: 'var(--predict-ink)',
                                          fontWeight: 500
                                        }),
                                        multiValueRemove: base => ({
                                          ...base,
                                          color: 'var(--predict-ink)',
                                          '&:hover': {
                                            backgroundColor: 'var(--predict-soft)',
                                            color: 'var(--predict-ink)'
                                          }
                                        })
                                      }}
                                      options={getUniqueCveOptions(filter.key)}
                                      value={(cveFilters[filter.key] || []).map(value => ({
                                        value,
                                        label: value
                                      }))}
                                      onChange={selected => {
                                        setCveFilters({
                                          ...cveFilters,
                                          [filter.key]: selected ? selected.map(item => item.value) : []
                                        });
                                        setCveCurrentPage(1);
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {overlayError && (
                            <Alert color="warning" className="mb-2">
                              {overlayError}
                            </Alert>
                          )}

                          {overrideError && (
                            <Alert color="danger" className="mb-2" role="alert">
                              {overrideError}
                            </Alert>
                          )}

                          {/* CVE Table */}
                          <Table bordered hover className="bst-data">
                            <thead>
                              <tr>
                                <th>CVE</th>
                                <th>Summary</th>
                                <th>Severity</th>
                                <th>CVSS Score</th>
                                {showComponentColumn && <ComponentColumnHeader id="bst-component-col-cves" />}
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getPaginatedCves().length > 0 ? (
                                getPaginatedCves().map((cve, index) => {
                                  const mergedState = effectiveState(cve.id, cveStates, overlay?.cves);
                                  const strike = isStruckOut(mergedState.value) ? 'text-decoration-line-through' : '';
                                  const componentData = showComponentColumn
                                    ? componentCellData(cve?.affected_components, {
                                        isAuto: mergedState.isAuto,
                                        hint: partialHintFor(cve.id, overlay?.partialHints?.cves),
                                        configuredCiCount: overlay?.configuredCiCount
                                      })
                                    : null;
                                  return (
                                    <tr key={index}>
                                      <td>
                                        <span className={strike}>
                                          <a
                                            href={`https://nvd.nist.gov/vuln/detail/${cve?.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {cve?.id || '-'}
                                          </a>
                                        </span>
                                      </td>
                                      <td>
                                        <span className={strike}>{cve?.title || '-'}</span>
                                      </td>
                                      <td>
                                        <span className={strike}>{cve?.severity || '-'}</span>
                                      </td>
                                      <td>
                                        <span className={strike}>{cve?.cvss_base_score || '-'}</span>
                                      </td>
                                      {showComponentColumn && (
                                        <ComponentCell data={componentData} tooltipId={`bst-comp-cve-${cve.id}`} />
                                      )}
                                      <td>
                                        <button
                                          className="bst-comment-link"
                                          onClick={e => {
                                            e.preventDefault();
                                            setShowCveComment(cve.id);
                                          }}
                                        >
                                          {userVersionData?.cves?.find(item => item.cveId === cve.id)?.comments
                                            ?.length || 'Add'}{' '}
                                          {userVersionData?.cves?.find(item => item.cveId === cve.id)?.comments
                                            ?.length === 1
                                            ? 'comment'
                                            : 'comments'}
                                        </button>
                                        <br />
                                        {cveStates[cve.id]?.edit ? (
                                          <>
                                            <Select
                                              styles={{
                                                control: provided => ({
                                                  ...provided,
                                                  width: 'auto',
                                                  minWidth: 'fit-content',
                                                  whiteSpace: 'nowrap',
                                                  maxWidth: '100%'
                                                }),
                                                menu: provided => ({
                                                  ...provided,
                                                  whiteSpace: 'nowrap',
                                                  width: 'auto'
                                                })
                                              }}
                                              isDisabled={savingBugCve}
                                              options={BUG_CVE_REVIEW_STATES}
                                              value={{ value: mergedState.value, label: mergedState.value }}
                                              onChange={async e => {
                                                setSavingBugCve(true);
                                                setOverrideError(null);
                                                let projectVersionId;
                                                try {
                                                  projectVersionId = await ensureVersionRow({
                                                    project: selectedProject,
                                                    version: selectedVersion,
                                                    userVersionData,
                                                    setVersionState: BZAPIService.bsaSetVersionState
                                                  });
                                                } catch (err) {
                                                  // Leave the issue as it was — auto-marked — rather
                                                  // than showing a state we failed to store.
                                                  setSavingBugCve(false);
                                                  setOverrideError('Could not save that change. Please try again.');
                                                  return;
                                                }
                                                await BZAPIService.bsaSetVersionBugCveState({
                                                  projectVersionId,
                                                  productName: selectedProject.productName,
                                                  productId: selectedProject.productId,
                                                  vendorId: selectedProject.vendorId,
                                                  isCve: true,
                                                  cveId: cve.id,
                                                  state: e.value
                                                });
                                                await fetchUserVersionState(selectedVersion);
                                                setSavingBugCve(false);
                                              }}
                                            />
                                            {savingBugCve && (
                                              <div className="my-2 text-center">
                                                <Spinner color="primary" />
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <button
                                            className="bst-status-link"
                                            onClick={e => {
                                              e.preventDefault();
                                              // Finding 2 (STRY0038263 final-review): same reasoning
                                              // as the bug control above — seed from the merged
                                              // state, translating the auto flavour to its human
                                              // equivalent, rather than hardcoding 'Not reviewed'.
                                              const seedValue =
                                                mergedState.value === AUTO_CONFIG_REMOVED
                                                  ? CONFIG_REMOVED
                                                  : mergedState.value;
                                              setCveStates({
                                                ...cveStates,
                                                [cve.id]: {
                                                  value: seedValue,
                                                  edit: true
                                                }
                                              });
                                            }}
                                          >
                                            {/* Partial-enablement hints moved to the Component
                                              column — see the bug table above. */}
                                            <span
                                              className={mergedState.isAuto ? 'bst-state-auto' : undefined}
                                              title={
                                                mergedState.isAuto
                                                  ? `No device runs ${mergedState.components.join(', ')}`
                                                  : undefined
                                              }
                                            >
                                              {mergedState.value}
                                            </span>
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={showComponentColumn ? '6' : '5'} className="text-center">
                                    {cveSearchTerm ? 'No CVEs match your search.' : 'No CVEs found.'}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>

                          {/* Pagination */}
                          {getCveTotalPages() > 1 && (
                            <div className="bst-pagination">
                              <button
                                className="bst-pagination-btn"
                                onClick={() => setCveCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={cveCurrentPage === 1}
                              >
                                <FontAwesomeIcon icon={faChevronLeft} />
                              </button>
                              <div className="bst-pagination-pages">
                                {Array.from({ length: getCveTotalPages() }, (_, i) => i + 1)
                                  .filter(page => {
                                    if (page === 1 || page === getCveTotalPages()) return true;
                                    if (Math.abs(page - cveCurrentPage) <= 1) return true;
                                    return false;
                                  })
                                  .reduce((acc, page, idx, arr) => {
                                    if (idx > 0 && page - arr[idx - 1] > 1) {
                                      acc.push('...');
                                    }
                                    acc.push(page);
                                    return acc;
                                  }, [])
                                  .map((item, idx) =>
                                    item === '...' ? (
                                      <span key={`ellipsis-${idx}`} className="bst-pagination-ellipsis">
                                        ...
                                      </span>
                                    ) : (
                                      <button
                                        key={item}
                                        className={`bst-pagination-page ${cveCurrentPage === item ? 'active' : ''}`}
                                        onClick={() => setCveCurrentPage(item)}
                                      >
                                        {item}
                                      </button>
                                    )
                                  )}
                              </div>
                              <button
                                className="bst-pagination-btn"
                                onClick={() => setCveCurrentPage(prev => Math.min(prev + 1, getCveTotalPages()))}
                                disabled={cveCurrentPage === getCveTotalPages()}
                              >
                                <FontAwesomeIcon icon={faChevronRight} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Compare Versions Tab Content */}
                {activeDetailTab === 'comparison' && (
                  <div className="bst-comparison-content">
                    <div className="bst-comparison-header">
                      <div className="bst-version-selector">
                        <div className="bst-version-slot">
                          <label>Current:</label>
                          <div className="bst-current-version-display">
                            <strong>{projectVersion?.label}</strong>
                          </div>
                        </div>
                        <span className="bst-vs-separator">vs</span>
                        <div className="bst-version-slot">
                          <label>Compare to:</label>
                          <Select
                            className="bst-version-dropdown"
                            options={upgradeVersions
                              .filter(v => v.suggestionId !== projectVersion?.value)
                              .map(v => ({ value: v.suggestionId, label: v.releaseVersion }))}
                            value={
                              comparisonVersions[0]
                                ? {
                                    value: comparisonVersions[0].suggestionId,
                                    label: comparisonVersions[0].releaseVersion
                                  }
                                : null
                            }
                            onChange={async e => {
                              const selected = upgradeVersions.find(v => v.suggestionId === e?.value);
                              setComparisonVersions([selected || null, comparisonVersions[1]]);
                              if (selected) {
                                await fetchComparisonVersionData(selected, 0);
                              }
                            }}
                            isClearable
                            placeholder="Select version..."
                          />
                        </div>
                        <span className="bst-vs-separator">vs</span>
                        <div className="bst-version-slot">
                          <Select
                            className="bst-version-dropdown"
                            options={upgradeVersions
                              .filter(
                                v =>
                                  v.suggestionId !== projectVersion?.value &&
                                  v.suggestionId !== comparisonVersions[0]?.suggestionId
                              )
                              .map(v => ({ value: v.suggestionId, label: v.releaseVersion }))}
                            value={
                              comparisonVersions[1]
                                ? {
                                    value: comparisonVersions[1].suggestionId,
                                    label: comparisonVersions[1].releaseVersion
                                  }
                                : null
                            }
                            onChange={async e => {
                              const selected = upgradeVersions.find(v => v.suggestionId === e?.value);
                              setComparisonVersions([comparisonVersions[0], selected || null]);
                              if (selected) {
                                await fetchComparisonVersionData(selected, 1);
                              }
                            }}
                            isClearable
                            placeholder="Select version..."
                          />
                        </div>
                      </div>
                    </div>

                    {comparisonVersions[0] || comparisonVersions[1] ? (
                      <>
                        <table className="bst-comparison-table">
                          <thead>
                            <tr>
                              <th>Metric</th>
                              <th className="bst-version-header">
                                <div className="version-name">
                                  {projectVersion?.label}
                                  <span className="bst-current-badge">Current</span>
                                </div>
                                <div className="version-date">
                                  {currentVersionData?.version?.releaseDate
                                    ? moment.utc(currentVersionData.version.releaseDate).format('MMM D, YYYY')
                                    : '-'}
                                </div>
                              </th>
                              {comparisonVersions[0] && (
                                <th className="bst-version-header">
                                  <div className="version-name">{comparisonVersions[0].releaseVersion}</div>
                                  <div className="version-date">
                                    {comparisonVersions[0].releaseDate
                                      ? moment.utc(comparisonVersions[0].releaseDate).format('MMM D, YYYY')
                                      : '-'}
                                  </div>
                                </th>
                              )}
                              {comparisonVersions[1] && (
                                <th className="bst-version-header">
                                  <div className="version-name">{comparisonVersions[1].releaseVersion}</div>
                                  <div className="version-date">
                                    {comparisonVersions[1].releaseDate
                                      ? moment.utc(comparisonVersions[1].releaseDate).format('MMM D, YYYY')
                                      : '-'}
                                  </div>
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="bst-metric-label">Operational Risk Score</td>
                              <td className="bst-metric-value">
                                {currentVersionLoading ? (
                                  <Spinner size="sm" color="primary" />
                                ) : currentVersionData ? (
                                  <span className="bst-risk-indicator">
                                    <span
                                      className={`bst-risk-dot ${getRiskClass(currentVersionData.bugAverage)}`}
                                    ></span>
                                    {currentVersionData.bugAverage || '-'}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              {comparisonVersions[0] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[0] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[0].suggestionId] ? (
                                    <>
                                      <span className="bst-risk-indicator">
                                        <span
                                          className={`bst-risk-dot ${getRiskClass(
                                            comparisonDataCache[comparisonVersions[0].suggestionId].bugAverage
                                          )}`}
                                        ></span>
                                        {comparisonDataCache[comparisonVersions[0].suggestionId].bugAverage || '-'}
                                      </span>
                                      {currentVersionData?.bugAverage &&
                                        comparisonDataCache[comparisonVersions[0].suggestionId].bugAverage && (
                                          <span
                                            className={`bst-delta-indicator ${
                                              comparisonDataCache[comparisonVersions[0].suggestionId].bugAverage <
                                              currentVersionData.bugAverage
                                                ? 'positive'
                                                : 'negative'
                                            }`}
                                          >
                                            {comparisonDataCache[comparisonVersions[0].suggestionId].bugAverage <
                                            currentVersionData.bugAverage
                                              ? '↓'
                                              : '↑'}{' '}
                                            {Math.abs(
                                              currentVersionData.bugAverage -
                                                comparisonDataCache[comparisonVersions[0].suggestionId].bugAverage
                                            ).toFixed(1)}
                                          </span>
                                        )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                              {comparisonVersions[1] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[1] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[1].suggestionId] ? (
                                    <>
                                      <span className="bst-risk-indicator">
                                        <span
                                          className={`bst-risk-dot ${getRiskClass(
                                            comparisonDataCache[comparisonVersions[1].suggestionId].bugAverage
                                          )}`}
                                        ></span>
                                        {comparisonDataCache[comparisonVersions[1].suggestionId].bugAverage || '-'}
                                      </span>
                                      {currentVersionData?.bugAverage &&
                                        comparisonDataCache[comparisonVersions[1].suggestionId].bugAverage && (
                                          <span
                                            className={`bst-delta-indicator ${
                                              comparisonDataCache[comparisonVersions[1].suggestionId].bugAverage <
                                              currentVersionData.bugAverage
                                                ? 'positive'
                                                : 'negative'
                                            }`}
                                          >
                                            {comparisonDataCache[comparisonVersions[1].suggestionId].bugAverage <
                                            currentVersionData.bugAverage
                                              ? '↓'
                                              : '↑'}{' '}
                                            {Math.abs(
                                              currentVersionData.bugAverage -
                                                comparisonDataCache[comparisonVersions[1].suggestionId].bugAverage
                                            ).toFixed(1)}
                                          </span>
                                        )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                            </tr>
                            <tr>
                              <td className="bst-metric-label">Security Risk Score</td>
                              <td className="bst-metric-value">
                                {currentVersionLoading ? (
                                  <Spinner size="sm" color="primary" />
                                ) : currentVersionData ? (
                                  <span className="bst-risk-indicator">
                                    <span
                                      className={`bst-risk-dot ${getRiskClass(currentVersionData.cveAverage)}`}
                                    ></span>
                                    {currentVersionData.cveAverage || '-'}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>
                              {comparisonVersions[0] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[0] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[0].suggestionId] ? (
                                    <>
                                      <span className="bst-risk-indicator">
                                        <span
                                          className={`bst-risk-dot ${getRiskClass(
                                            comparisonDataCache[comparisonVersions[0].suggestionId].cveAverage
                                          )}`}
                                        ></span>
                                        {comparisonDataCache[comparisonVersions[0].suggestionId].cveAverage || '-'}
                                      </span>
                                      {currentVersionData?.cveAverage &&
                                        comparisonDataCache[comparisonVersions[0].suggestionId].cveAverage && (
                                          <span
                                            className={`bst-delta-indicator ${
                                              comparisonDataCache[comparisonVersions[0].suggestionId].cveAverage <
                                              currentVersionData.cveAverage
                                                ? 'positive'
                                                : 'negative'
                                            }`}
                                          >
                                            {comparisonDataCache[comparisonVersions[0].suggestionId].cveAverage <
                                            currentVersionData.cveAverage
                                              ? '↓'
                                              : '↑'}{' '}
                                            {Math.abs(
                                              currentVersionData.cveAverage -
                                                comparisonDataCache[comparisonVersions[0].suggestionId].cveAverage
                                            ).toFixed(1)}
                                          </span>
                                        )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                              {comparisonVersions[1] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[1] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[1].suggestionId] ? (
                                    <>
                                      <span className="bst-risk-indicator">
                                        <span
                                          className={`bst-risk-dot ${getRiskClass(
                                            comparisonDataCache[comparisonVersions[1].suggestionId].cveAverage
                                          )}`}
                                        ></span>
                                        {comparisonDataCache[comparisonVersions[1].suggestionId].cveAverage || '-'}
                                      </span>
                                      {currentVersionData?.cveAverage &&
                                        comparisonDataCache[comparisonVersions[1].suggestionId].cveAverage && (
                                          <span
                                            className={`bst-delta-indicator ${
                                              comparisonDataCache[comparisonVersions[1].suggestionId].cveAverage <
                                              currentVersionData.cveAverage
                                                ? 'positive'
                                                : 'negative'
                                            }`}
                                          >
                                            {comparisonDataCache[comparisonVersions[1].suggestionId].cveAverage <
                                            currentVersionData.cveAverage
                                              ? '↓'
                                              : '↑'}{' '}
                                            {Math.abs(
                                              currentVersionData.cveAverage -
                                                comparisonDataCache[comparisonVersions[1].suggestionId].cveAverage
                                            ).toFixed(1)}
                                          </span>
                                        )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                            </tr>
                            <tr>
                              <td className="bst-metric-label">Total Bugs</td>
                              <td className="bst-metric-value">
                                {currentVersionLoading ? (
                                  <Spinner size="sm" color="primary" />
                                ) : currentVersionData ? (
                                  currentVersionData.bugs.length
                                ) : (
                                  '-'
                                )}
                              </td>
                              {comparisonVersions[0] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[0] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[0].suggestionId] ? (
                                    <>
                                      {comparisonDataCache[comparisonVersions[0].suggestionId].bugs.length}
                                      {currentVersionData && currentVersionData.bugs.length > 0 && (
                                        <span
                                          className={`bst-delta-indicator ${
                                            comparisonDataCache[comparisonVersions[0].suggestionId].bugs.length <
                                            currentVersionData.bugs.length
                                              ? 'positive'
                                              : 'negative'
                                          }`}
                                        >
                                          {comparisonDataCache[comparisonVersions[0].suggestionId].bugs.length -
                                            currentVersionData.bugs.length}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                              {comparisonVersions[1] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[1] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[1].suggestionId] ? (
                                    <>
                                      {comparisonDataCache[comparisonVersions[1].suggestionId].bugs.length}
                                      {currentVersionData && currentVersionData.bugs.length > 0 && (
                                        <span
                                          className={`bst-delta-indicator ${
                                            comparisonDataCache[comparisonVersions[1].suggestionId].bugs.length <
                                            currentVersionData.bugs.length
                                              ? 'positive'
                                              : 'negative'
                                          }`}
                                        >
                                          {comparisonDataCache[comparisonVersions[1].suggestionId].bugs.length -
                                            currentVersionData.bugs.length}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                            </tr>
                            <tr>
                              <td className="bst-metric-label">Total CVEs</td>
                              <td className="bst-metric-value">
                                {currentVersionLoading ? (
                                  <Spinner size="sm" color="primary" />
                                ) : currentVersionData ? (
                                  currentVersionData.cves.length
                                ) : (
                                  '-'
                                )}
                              </td>
                              {comparisonVersions[0] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[0] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[0].suggestionId] ? (
                                    <>
                                      {comparisonDataCache[comparisonVersions[0].suggestionId].cves.length}
                                      {currentVersionData && currentVersionData.cves.length > 0 && (
                                        <span
                                          className={`bst-delta-indicator ${
                                            comparisonDataCache[comparisonVersions[0].suggestionId].cves.length <
                                            currentVersionData.cves.length
                                              ? 'positive'
                                              : 'negative'
                                          }`}
                                        >
                                          {comparisonDataCache[comparisonVersions[0].suggestionId].cves.length -
                                            currentVersionData.cves.length}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                              {comparisonVersions[1] && (
                                <td className="bst-metric-value">
                                  {comparisonLoading[1] ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : comparisonDataCache[comparisonVersions[1].suggestionId] ? (
                                    <>
                                      {comparisonDataCache[comparisonVersions[1].suggestionId].cves.length}
                                      {currentVersionData && currentVersionData.cves.length > 0 && (
                                        <span
                                          className={`bst-delta-indicator ${
                                            comparisonDataCache[comparisonVersions[1].suggestionId].cves.length <
                                            currentVersionData.cves.length
                                              ? 'positive'
                                              : 'negative'
                                          }`}
                                        >
                                          {comparisonDataCache[comparisonVersions[1].suggestionId].cves.length -
                                            currentVersionData.cves.length}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                            </tr>
                            <tr>
                              <td className="bst-metric-label">Version Age</td>
                              <td className="bst-metric-value">
                                {currentVersionData?.version?.releaseDate
                                  ? moment.utc(currentVersionData.version.releaseDate).fromNow(true)
                                  : '-'}
                              </td>
                              {comparisonVersions[0] && (
                                <td className="bst-metric-value">
                                  {comparisonVersions[0].releaseDate
                                    ? moment.utc(comparisonVersions[0].releaseDate).fromNow(true)
                                    : '-'}
                                </td>
                              )}
                              {comparisonVersions[1] && (
                                <td className="bst-metric-value">
                                  {comparisonVersions[1].releaseDate
                                    ? moment.utc(comparisonVersions[1].releaseDate).fromNow(true)
                                    : '-'}
                                </td>
                              )}
                            </tr>
                            <tr>
                              <td className="bst-metric-label">Review Status</td>
                              <td className="bst-metric-value">
                                <span
                                  className={`bst-status-badge ${getStatusBadgeClass(
                                    getVersionState(projectVersion?.value)
                                  )}`}
                                >
                                  {getVersionState(projectVersion?.value)}
                                </span>
                              </td>
                              {comparisonVersions[0] && (
                                <td className="bst-metric-value">
                                  <span
                                    className={`bst-status-badge ${getStatusBadgeClass(
                                      getVersionState(comparisonVersions[0].suggestionId)
                                    )}`}
                                  >
                                    {getVersionState(comparisonVersions[0].suggestionId)}
                                  </span>
                                </td>
                              )}
                              {comparisonVersions[1] && (
                                <td className="bst-metric-value">
                                  <span
                                    className={`bst-status-badge ${getStatusBadgeClass(
                                      getVersionState(comparisonVersions[1].suggestionId)
                                    )}`}
                                  >
                                    {getVersionState(comparisonVersions[1].suggestionId)}
                                  </span>
                                </td>
                              )}
                            </tr>
                          </tbody>
                        </table>

                        {/* Recommendation Box */}
                        {getAcceptedVersion() && (
                          <div className="bst-recommendation-box">
                            <div className="bst-recommendation-icon">
                              <FontAwesomeIcon icon={faCheckCircle} />
                            </div>
                            <div className="bst-recommendation-content">
                              <h4>Accepted Version: {getAcceptedVersion()}</h4>
                              <p>
                                This version has been marked as accepted for this project. Consider using this as your
                                upgrade target.
                              </p>
                              <div className="bst-recommendation-reasons">
                                <span className="bst-reason-tag">Reviewed and approved</span>
                                <span className="bst-reason-tag">Team consensus</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="bst-comparison-empty">
                        <h4>Select versions to compare</h4>
                        <p>
                          Choose one or two versions from the dropdowns above to compare against the current version.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      {showBugComment && (
        <CommentModal
          isOpen={!!showBugComment}
          toggle={() => setShowBugComment(null)}
          isCve={false}
          userVersionData={userVersionData}
          projectVersionId={userVersionData.id}
          productName={selectedProject.productName}
          productId={selectedProject.productId}
          vendorId={selectedProject.vendorId}
          entityId={showBugComment}
          entityTitle={bugs.find(bug => bug.id === showBugComment)?.title || showBugComment}
          fetchUserVersionState={fetchUserVersionState}
          selectedVersion={selectedVersion}
        />
      )}
      {showCveComment && (
        <CommentModal
          isOpen={!!showCveComment}
          toggle={() => setShowCveComment(null)}
          isCve={true}
          userVersionData={userVersionData}
          projectVersionId={userVersionData.id}
          productName={selectedProject.productName}
          productId={selectedProject.productId}
          vendorId={selectedProject.vendorId}
          entityId={showCveComment}
          entityTitle={cves.find(cve => cve.id === showCveComment)?.title || showCveComment}
          fetchUserVersionState={fetchUserVersionState}
          selectedVersion={selectedVersion}
        />
      )}

      {showDeleteProject && (
        <DeleteProjectModal
          isOpen={!!showDeleteProject}
          toggle={() => setShowDeleteProject(null)}
          projects={projects}
          project={showDeleteProject}
          setProjects={setProjects}
        />
      )}

      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          toggle={() => setShowExportModal(false)}
          selectedProject={selectedProject}
          currentUserEmail={currentUserEmail}
          canCreateExport={canModifyProjects}
        />
      )}
    </Container>
  );
};

export default BugScrubbingTool;
